<template>
  <section class="card">
    <header class="head">
      <div>
        <h2>会员管理</h2>
      </div>
      <div class="head-actions">
        <button type="button" class="btn btn-edit" @click="openTagDialog()">批量打标签</button>
        <button type="button" class="btn btn-edit" :disabled="!canEdit" @click="downloadImportTemplate">下载导入模板</button>
        <input
          ref="importFileInput"
          type="file"
          class="sr-only"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          @change="onImportFile"
        />
        <button type="button" class="btn btn-edit" :disabled="!canEdit" @click="clickImportFile">Excel 导入</button>
        <button
          type="button"
          class="btn btn-edit"
          :disabled="!canEdit || repairDirtyBusy"
          :title="'将误降级为「已申请待审核」的正式会员恢复为正式会员，同步申请职务，并作废误生成的待付入会首次会费'"
          @click="repairSupplementaryDirty"
        >{{ repairDirtyBusy ? '修复中…' : '修复补表脏数据' }}</button>
        <button class="btn btn-primary" :disabled="!canEdit" @click="openCreate">新增会员</button>
      </div>
    </header>

    <p
      v-if="importSummary"
      class="import-summary"
      :class="importSummaryFail ? 'import-summary--fail' : 'import-summary--ok'"
    >{{ importSummary }}</p>
    <p class="import-hint muted">
      Excel 导入说明：请下载最新模板填写；仅支持 .xlsx。Chrome/Edge 下点「Excel 导入」会写回你选中的原文件第 1 列「导入结果」（请先关闭 Excel 再导入，完成后重新打开该文件查看）。其它浏览器会下载同名结果文件。第 1 行为表头；手机号已存在则该行不导入；注册人单位同名不报错。
    </p>

    <div
      v-if="needFillApplyToastVisible && needFillApplyMembers.length"
      class="need-fill-apply-toast"
      role="status"
    >
      <div class="need-fill-apply-toast__head">
        <strong>待补充入会申请表</strong>
        <button type="button" class="need-fill-apply-toast__close" aria-label="关闭" @click="dismissNeedFillApplyToast">×</button>
      </div>
      <p class="need-fill-apply-toast__desc">
        以下正式会员不是其他单位名册成员，且入会申请未补全（或缺单位地图选点）。请通知本人在小程序填写；地图选点仅手机端可完成。也可点姓名打开「查看申请表」代填文字项。
      </p>
      <ul class="need-fill-apply-toast__list">
        <li v-for="row in needFillApplyMembersPreview" :key="'need-fill-' + row.id">
          <button type="button" class="need-fill-apply-toast__link" @click="openNeedFillApplyMember(row)">
            {{ needFillApplyMemberLabel(row) }}
          </button>
        </li>
      </ul>
      <p v-if="needFillApplyMembers.length > needFillApplyMembersPreview.length" class="need-fill-apply-toast__more">
        另有 {{ needFillApplyMembers.length - needFillApplyMembersPreview.length }} 人…
      </p>
    </div>

    <div class="filters filters-panel">
      <div class="flt">
        <input
          v-model.trim="keywordQ"
          class="flt-input flt-input--wide"
          placeholder="姓名/手机号/会员编号/职务"
          aria-label="姓名、手机号、会员编号、职务"
          @keyup.enter="search"
        />
      </div>
      <div class="flt">
        <input
          v-model.trim="unitQ"
          class="flt-input"
          placeholder="入会单位"
          aria-label="入会单位"
          @keyup.enter="search"
        />
      </div>
      <div class="flt">
        <FancySelect v-model="memberLevelQ" class="flt-select" :options="memberLevelFilterOptions" placeholder="会员级别" />
      </div>
      <div class="flt">
        <FancySelect v-model="consultantQ" class="flt-select" :options="consultantFilterOptions" placeholder="服务专员" />
      </div>
      <div class="flt">
        <FancySelect v-model="businessStatusQ" class="flt-select" :options="businessStatusOptions" placeholder="业务状态" />
      </div>
      <div class="flt">
        <FancySelect
          v-model="feeStatusQ"
          class="flt-select"
          :options="feeStatusOptions"
          placeholder="缴费状态"
          @change="onFeeStatusChange"
        />
      </div>
      <div class="flt">
        <FancySelect v-model="loginStatusQ" class="flt-select" :options="loginStatusOptions" placeholder="登录状态" />
      </div>
      <div class="flt">
        <FancySelect v-model="tagFilterQ" class="flt-select" :options="memberTagFilterOptions" placeholder="用户标签" />
      </div>
      <div class="filter-actions">
        <button type="button" class="btn btn-primary filter-action-btn" @click="search">查询</button>
        <button type="button" class="btn btn-cancel filter-action-btn" @click="resetFilters">重置</button>
      </div>
    </div>

    <p v-if="errorMsg && !showDialog" class="error">{{ errorMsg }}</p>

    <div class="table-scroll">
      <table class="member-table">
        <thead>
          <tr>
            <th class="col-check">
              <input type="checkbox" :checked="allChecked" @change="toggleCheckAll" />
            </th>
            <th class="col-seq">序号</th>
            <th class="col-center col-company">入会单位</th>
            <th class="col-rep">会员代表</th>
            <th class="col-center col-level">会员级别</th>
            <th class="col-consultant">服务专员</th>
            <th class="col-status">业务状态</th>
            <th class="col-status">缴费状态</th>
            <th class="col-money" title="待缴费/待审显示订单金额；其余状态优先显示会费定价表中该级别金额">会费(元)</th>
            <th class="col-reg">注册者信息</th>
            <th class="col-status">登录状态</th>
            <th class="col-date">有效期</th>
            <th class="col-num">订单数</th>
            <th class="col-num">活动数</th>
            <th class="col-datetime">创建时间</th>
            <th class="col-tags">标签</th>
            <th class="col-sticky-right col-ops">操作</th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, idx) in paginatedList"
            :key="row.id"
            :ref="'memberRow-' + row.id"
            :class="{ 'row-highlight': Number(highlightedMemberId) === Number(row.id) }"
          >
            <td class="col-check"><input type="checkbox" :checked="isChecked(row.id)" @change="toggleChecked(row.id)" /></td>
            <td class="col-seq">{{ rowOffset + idx + 1 }}</td>
            <td class="col-center col-company">
              {{ row.companyName || row.registerCompanyName || '-' }}
            </td>
            <td class="col-rep">
              <div>{{ representativeNameText(row) }}</div>
              <div class="sub">{{ representativeMobileText(row) }}</div>
            </td>
            <td class="col-center col-level">{{ row.memberLevel || '-' }}</td>
            <td class="col-consultant">{{ row.serviceConsultant || '-' }}</td>
            <td class="col-status">
              <span class="status-pill" :class="businessStatusClass(row)">
                {{ businessStatusText(row) }}
              </span>
              <div v-if="businessStatusHint(row)" class="status-sub-hint">{{ businessStatusHint(row) }}</div>
            </td>
            <td class="col-status">
              <span class="status-pill" :class="feeStatusClass(row.feeStatus)">
                {{ displayFeeStatusLabel(row) }}
              </span>
              <div v-if="feeStatusHint(row)" class="fee-status-hint">{{ feeStatusHint(row) }}</div>
            </td>
            <td class="col-money">
              <template v-if="feeMoneyMissingPricing(row)">
                <button type="button" class="fee-miss-link" @click="goFeePricingMaintain">该级别会费未维护</button>
                <div class="fee-miss-hint">请到：会员 → 模板管理 → 会费定价<br />维护后此处自动显示</div>
              </template>
              <span v-else :title="feeMoneyTitle(row)">{{ feeMoneyText(row) }}</span>
            </td>
            <td class="col-reg">
              <div>{{ registerNameText(row) }}</div>
              <div class="sub">{{ registerMobileText(row) }}</div>
              <div class="sub">{{ registerTitleText(row) }}</div>
              <div class="sub">{{ registerCompanyText(row) }}</div>
            </td>
            <td class="col-status">
              <span class="status-pill" :class="row.online ? 'status-pill--active' : 'status-pill--idle'">
                {{ row.online ? '在线' : '离线' }}
              </span>
            </td>
            <td class="col-date">{{ row.validUntil || '-' }}</td>
            <td class="col-num">{{ row.orderCount ?? 0 }}</td>
            <td class="col-num">{{ row.activityCount ?? 0 }}</td>
            <td class="col-datetime">{{ formatDateTime(row.createdAt) }}</td>
            <td class="col-tags">
              <div v-if="memberTagNames(row).length" class="tag-cell">
                <span v-for="name in memberTagNames(row)" :key="name" class="member-tag-pill">{{ name }}</span>
              </div>
              <span v-else class="muted">—</span>
            </td>
            <td class="col-sticky-right col-ops">
              <div class="col-ops-grid">
                <button type="button" class="op-btn op-btn--bind" @click="showAuthIdentities(row)">账号绑定</button>
                <button
                  v-if="isSuperAdmin"
                  type="button"
                  class="op-btn op-btn--primary"
                  @click="openResetLocalPassword(row)"
                >重置登录密码</button>
                <button
                  v-if="memberRowShowUploadFeeVoucher(row)"
                  type="button"
                  class="op-btn op-btn--primary"
                  @click="onUploadFeeVoucher(row)"
                >帮他上传缴费凭证</button>
                <button
                  v-if="memberRowShowReviewFeeVoucher(row)"
                  type="button"
                  class="op-btn op-btn--fee"
                  @click="onReviewFeeVoucher(row)"
                >审核缴费凭证</button>
                <button
                  v-if="memberRowShowViewFeeVoucher(row)"
                  type="button"
                  class="op-btn op-btn--fee"
                  @click="onViewFeeVoucher(row)"
                >查看缴费凭证</button>
                <button
                  type="button"
                  class="op-btn op-btn--records"
                  @click="openPaymentRecords(row)"
                >缴费记录</button>
                <button
                  v-if="memberRowShowEnsureRenewal(row)"
                  type="button"
                  class="op-btn op-btn--renewal"
                  :disabled="!canEdit"
                  title="为正式会员生成年度续费待支付订单"
                  @click="onEnsureRenewal(row)"
                >续费设置</button>
                <button
                  type="button"
                  class="op-btn op-btn--apply"
                  title="查看并编辑该会员入会申请表（无记录时将创建空白申请表）"
                  @click="viewMembershipApply(row)"
                >查看申请表</button>
                <button
                  v-if="memberRowShowPersonnel(row)"
                  type="button"
                  class="op-btn op-btn--personnel"
                  title="代管该单位成员信息：待审同意入册、正式成员移出等"
                  @click="openPersonnel(row)"
                >成员信息</button>
                <button
                  type="button"
                  class="op-btn op-btn--seal"
                  :title="
                    memberHasSealFile(row)
                      ? `查看盖章扫描件${row.membershipApplySealFileName ? '：' + row.membershipApplySealFileName : ''}`
                      : '会员尚未上传盖章扫描件'
                  "
                  @click="viewMembershipApplySealFile(row)"
                >查看盖章件</button>
                <button type="button" class="op-btn op-btn--edit" :disabled="!canEdit" @click="openEdit(row)">编辑</button>
                <button type="button" class="op-btn op-btn--tag" @click="openTagDialog(row)">打标签</button>
                <button
                  type="button"
                  class="op-btn op-btn--kick"
                  :disabled="!canEdit || !row.online"
                  :title="row.online ? '使当前会员登录会话失效' : '当前会员不在线'"
                  @click="revokeSessions(row)"
                >踢下线</button>
                <button
                  v-if="isWithdrawnMember(row)"
                  type="button"
                  class="op-btn op-btn--delete"
                  :disabled="!canEdit"
                  title="永久删除已退会会员账号及相关数据"
                  @click="deleteWithdrawnMember(row)"
                >删除</button>
              </div>
            </td>
          </tr>
          <tr v-if="!paginatedList.length">
            <td class="table-empty" colspan="17">暂无符合条件的会员</td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPager
      v-model:page="page"
      v-model:page-size="pageSize"
      :total="totalCount"
    />

    <div v-if="showDialog" class="mask" @click.self="showDialog = false">
      <div class="dialog dialog--shell dialog--lg" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">{{ editId ? '编辑会员' : '新增会员' }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="showDialog = false">×</button>
        </div>
        <div class="dialog-body">
          <p v-if="applyDutyOptionsLoadError" class="dialog-hint muted">{{ applyDutyOptionsLoadError }}</p>
          <p v-if="errorMsg" class="error dialog-form-error">{{ errorMsg }}</p>
          <div class="form">
          <label class="field field--disabled">
            <span>会员编号：</span>
            <input
              :value="memberCodeDisplay"
              class="form-control"
              readonly
              disabled
            >
            <small v-if="!form.memberCode" class="field-hint">成为正式会员后由系统自动生成</small>
          </label>
          <label class="field"><span>姓名：</span><input v-model.trim="form.nickname" class="form-control" placeholder="必填"></label>
          <div class="field-row field-full">
            <label class="field">
              <span>注册手机号：</span>
              <input v-model.trim="form.mobile" class="form-control" maxlength="11" placeholder="必填，11 位；与微信授权号一致方可微信登录原档案">
            </label>
            <label class="field">
              <span>会员排序：</span>
              <input v-model.number="form.memberSort" class="form-control" type="number" min="1" step="1" placeholder="默认 1，越小越靠前">
            </label>
          </div>
          <small class="field-hint field-full">改号后同步登录身份；若曾绑错微信，请用「账号绑定」解绑后让用户重新授权。</small>
          <label class="field"><span>注册人单位：</span><input v-model.trim="form.companyName" class="form-control" placeholder="必填"></label>
          <label class="field">
            <span>注册人单位职务：</span>
            <input v-model.trim="form.registerTitle" class="form-control" maxlength="64" placeholder="必填" />
          </label>
          <label class="field">
            <span>服务专员：</span>
            <FancySelect
              v-model="form.serviceConsultant"
              class="field-select"
              :options="serviceConsultantOptions"
              placeholder="从「专员」型后台用户中选择"
              searchable
              clearable
            />
          </label>
          <label class="field">
            <span>会员级别：</span>
            <FancySelect
              v-model="form.memberLevel"
              class="field-select"
              :options="memberLevelDutyOptions"
              placeholder="选择会员级别"
              searchable
            />
          </label>
          <label class="field">
            <span>状态：</span>
            <FancySelect v-model="form.status" class="field-select" :options="statusOptions" placeholder="选择状态" />
          </label>
          <label class="field" :class="{ 'field--disabled': !validUntilEditable }">
            <span>有效期：</span>
            <input
              v-model="form.validUntil"
              class="form-control form-control--date"
              type="date"
              :disabled="!validUntilEditable"
              :placeholder="validUntilEditable ? '请选择到期日' : '仅正式会员有有效期'"
              @click="openNativeDatePicker"
              @change="onValidUntilChange"
            >
            <small v-if="validUntilEditable && form.validUntilManual" class="field-hint">已手动设置；续费审核通过后将在该日期基础上顺延一年。</small>
            <small v-else-if="validUntilEditable" class="field-hint">默认按会费审核记录计算；可直接修改，保存后以您填写的为准。</small>
          </label>
          <div class="field-check-row field-full">
            <label class="field field-check">
              <span>单位在单位列表展示</span>
              <input v-model="form.showInUnitList" type="checkbox">
            </label>
            <label class="field field-check">
              <span>负责人在人脉列表展示</span>
              <input v-model="form.showInNetworkList" type="checkbox">
            </label>
            <label class="field field-check">
              <span>退会</span>
              <input v-model="form.withdrawn" type="checkbox">
            </label>
          </div>
          <small class="field-hint field-full">退会后仍保留在列表，可筛选「已退会」；取消勾选可恢复退会前状态。</small>
        </div>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" @click="showDialog = false">取消</button>
          <button type="button" class="btn btn-primary" @click="submit">保存</button>
        </div>
      </div>
    </div>

    <div v-if="feeVoucherDlg.visible" class="mask" @click.self="closeFeeVoucherAudit">
      <div class="dialog dialog--shell dialog--md dialog-fee-voucher" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">{{ feeVoucherDialogTitle }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeFeeVoucherAudit">×</button>
        </div>
        <div class="dialog-body">
          <p class="dlg-meta">{{ feeVoucherDlg.meta }}</p>
          <p class="dlg-meta">缴费状态：{{ feeVoucherDlg.feeStatusLabel }}</p>
          <p v-if="feeVoucherDlg.rejectNote" class="preview-reject-wrap dlg-reject">
            <span class="preview-reject-label">驳回原因</span>
            <span class="preview-reject-text">{{ feeVoucherDlg.rejectNote }}</span>
          </p>
          <div class="voucher-img-wrap">
            <img v-if="feeVoucherDlg.img" class="voucher-img" :src="feeVoucherDlg.img" alt="会费缴费凭证">
            <template v-else-if="feeVoucherDlg.canUpload">
              <button
                type="button"
                class="voucher-upload-add"
                :disabled="feeVoucherDlg.busy"
                @click="pickFeeVoucherFile"
              >
                <span class="voucher-upload-plus">+</span>
                <span class="voucher-upload-label">{{ feeVoucherDlg.busy ? '上传中…' : '上传缴费凭证' }}</span>
              </button>
              <p v-if="feeVoucherDlg.mockVoucher" class="muted mock-voucher-hint">会员已线上缴费，请补录到账凭证截图后再审核</p>
              <p v-else-if="feeVoucherDlg.uploadOnly" class="muted mock-voucher-hint">选择图片后可预览，确认后才会提交</p>
              <p v-else class="muted mock-voucher-hint">尚未上传凭证，点击上方区域选择图片</p>
            </template>
            <p v-else class="muted">暂无凭证图片</p>
          </div>
          <p v-if="feeVoucherDlg.uploadOnly && feeVoucherDlg.img" class="muted mock-voucher-hint">
            <button type="button" class="btn-link" :disabled="feeVoucherDlg.busy" @click="pickFeeVoucherFile">重新选择图片</button>
          </p>
          <input
            ref="feeVoucherFileInput"
            type="file"
            accept="image/*,application/pdf,.doc,.docx"
            class="sr-only"
            @change="onFeeVoucherFilePicked"
          >
          <p v-if="feeVoucherDlg.errorMsg" class="error">{{ feeVoucherDlg.errorMsg }}</p>
          <p v-if="!feeVoucherDlg.canReview && !feeVoucherDlg.canUpload" class="muted dlg-foot-hint">该订单已处理，仅可查看凭证。</p>
        </div>
        <div v-if="feeVoucherDlg.canReview" class="dialog-foot">
          <button type="button" class="btn btn-cancel" :disabled="!canEdit || feeVoucherDlg.busy" @click="closeFeeVoucherAudit">取消</button>
          <button type="button" class="btn btn-delete" :disabled="!canEdit || feeVoucherDlg.busy" @click="submitFeeVoucherReject">驳回</button>
          <button type="button" class="btn btn-primary" :disabled="!canEdit || feeVoucherDlg.busy" @click="submitFeeVoucherApprove">
            {{ feeVoucherDlg.busy ? '提交中…' : '审核通过' }}
          </button>
        </div>
        <div v-else-if="feeVoucherDlg.uploadOnly" class="dialog-foot">
          <button type="button" class="btn btn-cancel" :disabled="feeVoucherDlg.busy" @click="closeFeeVoucherAudit">取消</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="!canEdit || feeVoucherDlg.busy || !feeVoucherDlg.pendingVoucherUrl"
            @click="confirmFeeVoucherUpload"
          >
            {{ feeVoucherDlg.busy ? '提交中…' : '确认' }}
          </button>
        </div>
        <div v-else-if="feeVoucherDlg.canUpload" class="dialog-foot">
          <button type="button" class="btn btn-cancel" :disabled="feeVoucherDlg.busy" @click="closeFeeVoucherAudit">关闭</button>
        </div>
        <div v-else class="dialog-foot">
          <button type="button" class="btn btn-cancel" @click="closeFeeVoucherAudit">关闭</button>
        </div>
      </div>
    </div>

    <div v-if="showResetPwdDialog" class="mask" @click.self="closeResetLocalPassword">
      <div class="dialog dialog--shell dialog--md" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">重置小程序登录密码</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeResetLocalPassword">×</button>
        </div>
        <div class="dialog-body">
          <p v-if="resetPwdError" class="error">{{ resetPwdError }}</p>
          <p class="dlg-meta">会员：{{ resetPwdForm.name || '-' }}（ID {{ resetPwdForm.memberId || '-' }}）</p>
          <p class="dlg-meta">登录手机号：{{ resetPwdForm.mobile || '（未设置注册手机号）' }}</p>
          <div class="form">
            <label class="field field-full">
              <span>新密码</span>
              <input
                v-model="resetPwdForm.password"
                type="password"
                autocomplete="new-password"
                placeholder="6～64 位；无需对方旧密码"
              >
            </label>
            <label class="field field-full">
              <span>确认新密码</span>
              <input
                v-model="resetPwdForm.password2"
                type="password"
                autocomplete="new-password"
                placeholder="再次输入新密码"
              >
            </label>
          </div>
          <p class="field-hint">仅超级管理员可用。若该会员尚未注册小程序账号，将按注册手机号自动开通；设置后对方用「手机号 + 新密码」登录，旧会话会失效。</p>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" :disabled="resetPwdBusy" @click="closeResetLocalPassword">取消</button>
          <button type="button" class="btn btn-primary" :disabled="resetPwdBusy" @click="submitResetLocalPassword">
            {{ resetPwdBusy ? '保存中…' : '保存并生效' }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="showIdentityDialog" class="mask" @click.self="showIdentityDialog = false">
      <div class="dialog dialog--shell dialog--md" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">账号绑定（会员ID: {{ identityMemberId }}）</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="showIdentityDialog = false">×</button>
        </div>
        <div class="dialog-body">
          <p v-if="identityErrorMsg" class="error">{{ identityErrorMsg }}</p>
          <div v-if="isSuperAdmin" class="identity-pwd-bar">
            <button type="button" class="btn btn-primary" @click="openResetLocalPasswordFromIdentity">重置登录密码</button>
            <span class="muted">无需对方旧密码；未开通小程序账号时会自动开通</span>
          </div>
          <table>
          <thead>
            <tr>
              <th>类型</th>
              <th>标识</th>
              <th>验证时间</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="x in identityList" :key="x.id">
              <td>{{ x.identityType || '-' }}</td>
              <td class="ellipsis" :title="x.identityKey || ''">{{ x.identityKey || '-' }}</td>
              <td>{{ formatDateTime(x.verifiedAt) }}</td>
              <td><button class="btn btn-delete" :disabled="!canEdit" @click="unbindIdentity(x)">解绑</button></td>
            </tr>
            <tr v-if="!identityList.length">
              <td colspan="4">暂无绑定记录</td>
            </tr>
          </tbody>
        </table>
        <h4 style="margin: 12px 0 8px">解绑审计日志</h4>
        <table>
          <thead>
            <tr>
              <th>时间</th>
              <th>管理员ID</th>
              <th>动作</th>
              <th>详情</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="x in identityLogList" :key="x.id">
              <td>{{ formatDateTime(x.createdAt) }}</td>
              <td>{{ x.adminUserId ?? '-' }}</td>
              <td>{{ x.action || '-' }}</td>
              <td class="ellipsis" :title="x.detailJson || ''">{{ x.detailJson || '-' }}</td>
            </tr>
            <tr v-if="!identityLogList.length">
              <td colspan="4">暂无审计日志</td>
            </tr>
          </tbody>
        </table>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" @click="showIdentityDialog = false">关闭</button>
        </div>
      </div>
    </div>

    <div v-if="tagDlg.visible" class="mask" @click.self="closeTagDialog">
      <div class="dialog dialog--shell dialog--md dialog-tag" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">添加标签</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeTagDialog">×</button>
        </div>
        <div class="dialog-body">
          <p class="dlg-meta">已选用户（{{ tagDlg.memberIds.length }}人）</p>
          <div class="tag-dialog-toolbar">
            <span class="tag-dialog-label">全部标签（可多选）</span>
            <button type="button" class="btn btn-primary btn-sm" :disabled="tagDlg.busy" @click="promptNewTag">新增标签</button>
          </div>
          <div class="tag-chip-grid">
            <label v-for="tag in memberTags" :key="tag.id" class="tag-chip" :class="{ 'tag-chip--on': tagDlg.selectedTagIds.includes(tag.id) }">
              <input v-model="tagDlg.selectedTagIds" type="checkbox" :value="tag.id">
              <span>{{ tag.tagName }}</span>
            </label>
            <p v-if="!memberTags.length" class="muted">暂无标签，请先新增</p>
          </div>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" :disabled="tagDlg.busy" @click="closeTagDialog">取消</button>
          <button type="button" class="btn btn-primary" :disabled="tagDlg.busy || !tagDlg.selectedTagIds.length" @click="submitTagDialog">确定</button>
        </div>
      </div>
    </div>

    <div v-if="renewalDlg.visible" class="mask" @click.self="closeRenewalConfirm">
      <div class="dialog dialog--shell dialog--lg dialog-renewal" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">{{ renewalDlg.successMessage ? '操作成功' : '续费设置' }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeRenewalConfirm">×</button>
        </div>
        <div class="dialog-body">
          <p v-if="renewalDlg.successMessage" class="dlg-meta">{{ renewalDlg.successMessage }}</p>
          <p v-else-if="renewalDlg.loading" class="muted">加载中…</p>
          <p v-else-if="renewalDlg.errorMsg && !renewalDlg.form.companyName" class="error">{{ renewalDlg.errorMsg }}</p>
          <template v-else>
            <div class="renewal-grid">
              <label class="renewal-field renewal-field--readonly">
                <span class="renewal-label">会员名称</span>
                <span class="renewal-readonly">{{ renewalDlg.form.companyName || '—' }}</span>
              </label>
              <label class="renewal-field renewal-field--readonly">
                <span class="renewal-label">加入时间</span>
                <span class="renewal-readonly">{{ renewalDlg.form.joinDate || '—' }}</span>
              </label>
              <label class="renewal-field renewal-field--readonly">
                <span class="renewal-label">到期时间</span>
                <span class="renewal-readonly">{{ renewalDlg.form.validUntil || '—' }}</span>
              </label>
              <label class="renewal-field renewal-field--readonly">
                <span class="renewal-label">当前缴费状态</span>
                <span class="renewal-readonly">{{ renewalDlg.form.feeStatusLabel || '—' }}</span>
              </label>
              <label class="renewal-field renewal-field--readonly">
                <span class="renewal-label">当前职务</span>
                <span class="renewal-readonly">{{ renewalDlg.form.currentMemberLevel || '—' }}</span>
              </label>
              <label class="renewal-field renewal-field--readonly">
                <span class="renewal-label">缴费标准</span>
                <span class="renewal-readonly">{{ renewalDlg.form.feeStandardText || '—' }}</span>
              </label>
              <label class="renewal-field renewal-field--readonly">
                <span class="renewal-label">应缴金额</span>
                <span class="renewal-readonly">{{ renewalAmountDisplay }}</span>
              </label>
              <label class="renewal-field">
                <span class="renewal-label renewal-label--req">续期职务</span>
                <select v-model="renewalDlg.form.renewalLevel" class="renewal-input" @change="onRenewalPreviewFieldsChange">
                  <option v-for="lv in renewalDlg.levelOptions" :key="lv" :value="lv">{{ lv }}</option>
                </select>
              </label>
              <label class="renewal-field">
                <span class="renewal-label renewal-label--req">开始时间</span>
                <input v-model="renewalDlg.form.startDate" type="date" class="renewal-input" @change="onRenewalPreviewFieldsChange">
              </label>
              <label class="renewal-field">
                <span class="renewal-label renewal-label--req">结束时间</span>
                <input v-model="renewalDlg.form.endDate" type="date" class="renewal-input" @change="onRenewalEndDateManual">
              </label>
              <div class="renewal-field renewal-field--full">
                <span class="renewal-label renewal-label--req">续费设置</span>
                <div class="renewal-radio-row">
                  <label class="renewal-radio">
                    <input v-model="renewalDlg.form.paymentMode" type="radio" value="PENDING">
                    <span>设为待缴费</span>
                  </label>
                  <label class="renewal-radio">
                    <input v-model="renewalDlg.form.paymentMode" type="radio" value="PAID">
                    <span>设为已缴费</span>
                  </label>
                </div>
              </div>
              <div class="renewal-field renewal-field--full">
                <span class="renewal-label" :class="{ 'renewal-label--req': renewalDlg.form.paymentMode === 'PAID' }">缴费凭证</span>
                <div class="voucher-img-wrap renewal-voucher-wrap">
                  <img v-if="renewalDlg.voucherPreview" class="voucher-img" :src="renewalDlg.voucherPreview" alt="缴费凭证">
                  <button
                    v-else
                    type="button"
                    class="voucher-upload-add"
                    :disabled="renewalDlg.busy"
                    @click="pickRenewalVoucherFile"
                  >
                    <span class="voucher-upload-plus">+</span>
                    <span class="voucher-upload-label">{{ renewalDlg.busy ? '上传中…' : '上传缴费凭证' }}</span>
                  </button>
                </div>
                <p class="muted mock-voucher-hint">
                  {{ renewalDlg.form.paymentMode === 'PAID' ? '设为已缴费须上传凭证' : '待缴费时可代为上传凭证（选填）' }}
                </p>
                <p v-if="renewalDlg.voucherPreview" class="muted mock-voucher-hint">
                  <button type="button" class="btn-link" :disabled="renewalDlg.busy" @click="pickRenewalVoucherFile">重新选择图片</button>
                </p>
                <input
                  ref="renewalVoucherFileInput"
                  type="file"
                  accept="image/*"
                  class="sr-only"
                  @change="onRenewalVoucherFilePicked"
                >
              </div>
            </div>
            <p v-if="renewalDlg.errorMsg" class="error">{{ renewalDlg.errorMsg }}</p>
          </template>
        </div>
        <div class="dialog-foot">
          <template v-if="renewalDlg.successMessage">
            <button type="button" class="btn btn-primary" @click="closeRenewalConfirm">确定</button>
          </template>
          <template v-else>
            <button type="button" class="btn btn-cancel" :disabled="renewalDlg.busy || renewalDlg.loading" @click="closeRenewalConfirm">取消</button>
            <button type="button" class="btn btn-primary" :disabled="!canEdit || renewalDlg.busy || renewalDlg.loading" @click="submitRenewalConfirm">
              {{ renewalDlg.busy ? '提交中…' : '确认' }}
            </button>
          </template>
        </div>
      </div>
    </div>

    <div v-if="paymentRecordsDlg.visible" class="mask" @click.self="closePaymentRecords">
      <div class="dialog dialog--shell dialog--lg dialog-payment-records" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">缴费记录</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closePaymentRecords">×</button>
        </div>
        <div class="dialog-body">
          <p class="dlg-meta">{{ paymentRecordsDlg.meta }}</p>
          <p v-if="paymentRecordsDlg.loading" class="muted">加载中…</p>
          <p v-else-if="paymentRecordsDlg.error" class="error">{{ paymentRecordsDlg.error }}</p>
          <template v-else>
            <section class="pay-records-sec">
              <h4 class="pay-records-sec-title">会费订单</h4>
              <div v-if="!paymentRecordsDlg.feeOrders.length" class="muted pay-records-empty">暂无会费订单</div>
              <div v-for="o in paymentRecordsDlg.feeOrders" :key="'fee-' + o.id" class="pay-records-item">
                <div class="pay-records-head">
                  <span class="pay-records-title">{{ o.title || '会费订单' }}</span>
                  <span class="pay-records-status">{{ o.statusLabel || o.status }}</span>
                </div>
                <div class="pay-records-meta">订单号：#{{ o.id }} · 金额：{{ o.amountText || '—' }}</div>
                <div v-if="o.createdAt" class="pay-records-meta">创建时间：{{ o.createdAt }}</div>
                <div v-if="o.dueAt" class="pay-records-meta">应付截止：{{ o.dueAt }}</div>
                <div v-if="o.reviewNote" class="pay-records-meta">备注：{{ o.reviewNote }}</div>
                <div v-if="o.paymentVoucherUrl && !String(o.paymentVoucherUrl).startsWith('mock://')" class="pay-records-meta">
                  <img class="pay-records-voucher" :src="feeVoucherAbs(o.paymentVoucherUrl)" alt="缴费凭证">
                </div>
              </div>
            </section>
            <section class="pay-records-sec">
              <h4 class="pay-records-sec-title">活动费订单</h4>
              <p class="muted pay-records-hint">活动费凭证审核请在「活动管理 → 报名审核」处理。</p>
              <div v-if="!paymentRecordsDlg.activityOrders.length" class="muted pay-records-empty">暂无活动费订单</div>
              <div v-for="o in paymentRecordsDlg.activityOrders" :key="'act-' + o.id" class="pay-records-item">
                <div class="pay-records-head">
                  <span class="pay-records-title">{{ o.title || '活动费订单' }}</span>
                  <span class="pay-records-status">{{ o.statusLabel || o.status }}</span>
                </div>
                <div class="pay-records-meta">订单号：#{{ o.id }} · 金额：{{ o.amountText || '—' }}</div>
                <div v-if="o.createdAt" class="pay-records-meta">创建时间：{{ o.createdAt }}</div>
                <div v-if="o.reviewNote" class="pay-records-meta">备注：{{ o.reviewNote }}</div>
                <div v-if="o.paymentVoucherUrl && !String(o.paymentVoucherUrl).startsWith('mock://')" class="pay-records-meta">
                  <img class="pay-records-voucher" :src="feeVoucherAbs(o.paymentVoucherUrl)" alt="缴费凭证">
                </div>
              </div>
            </section>
          </template>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-primary" @click="closePaymentRecords">关闭</button>
        </div>
      </div>
    </div>

    <div v-if="applyPreviewVisible" class="mask" @click.self="closeApplyPreview">
      <div class="dialog dialog--shell dialog--xl dialog-apply-preview" @click.stop>
        <div class="dialog-head">
          <h3 class="dialog-title">入会申请表</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="closeApplyPreview">×</button>
        </div>
        <div class="dialog-body">
          <div v-if="applyPreviewLoading" class="muted">加载中…</div>
          <p v-else-if="applyPreviewError" class="error">{{ applyPreviewError }}</p>
          <template v-else>
            <div v-if="applyPreviewRow" class="preview-meta">
              <div>记录ID：{{ applyPreviewRow.id }}</div>
              <div>填表人（申请人）：{{ applyPreviewRow.submitterName || '—' }}</div>
              <div>申请人单位：{{ applyPreviewRow.registrantUnit || '—' }}</div>
              <div v-if="applyPreviewRow.representativeName">会员代表：{{ applyPreviewRow.representativeName }}</div>
              <div v-if="applyPreviewRow.applyUnit">入会单位：{{ applyPreviewRow.applyUnit }}</div>
              <div>提交时间：{{ formatDateTime(applyPreviewRow.createdAt) }}</div>
              <div v-if="applyPreviewRow.pipelineStatusLabel">流程状态：{{ applyPreviewRow.pipelineStatusLabel }}</div>
              <div v-if="applyPreviewApplyRejectNote" class="preview-reject-wrap">
                <span class="preview-reject-label">驳回原因</span>
                <span class="preview-reject-text">{{ applyPreviewApplyRejectNote }}</span>
              </div>
              <div v-if="applyPreviewFeeRejectNote" class="preview-reject-wrap">
                <span class="preview-reject-label">缴费驳回原因</span>
                <span class="preview-reject-text">{{ applyPreviewFeeRejectNote }}</span>
              </div>
            </div>
          <div class="preview-list">
            <div
              v-for="(item, idx) in applyPreviewItems"
              :key="`${idx}-${item.key}`"
              class="preview-item"
              :class="{ 'preview-item--group': String(item.key || '').startsWith('【') }"
            >
              <div class="preview-k">{{ item.key }}</div>
              <div class="preview-v">
                <template v-if="item.viewType === 'images'">
                  <div class="media-grid">
                    <img
                      v-for="(u, i) in item.urls"
                      :key="`${item.key}-img-${i}`"
                      class="media-thumb"
                      :src="u"
                      alt="img"
                      @click="openApplyPreviewLink(u)"
                    />
                  </div>
                </template>
                <template v-else-if="item.viewType === 'file'">
                  <button type="button" class="btn btn-edit" @click="openApplyPreviewLink(item.fileUrl)">查看文件</button>
                  <span class="file-name">{{ item.fileName || '附件' }}</span>
                </template>
                <template v-else>
                  {{ item.value }}
                </template>
              </div>
            </div>
            <p v-if="!applyPreviewItems.length" class="muted">暂无可展示字段</p>
          </div>
        </template>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" @click="closeApplyPreview">关闭</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { adminConfirm, adminPrompt } from '@/utils/adminDialog'
import FancySelect from '@/components/FancySelect.vue'
import ListPager from '@/components/ListPager.vue'
import { clampPage, pageRowOffset, slicePageRows } from '@/utils/listPager'
import {
  BASE_URL,
  toMediaUrl,
  createAdminMember,
  canWriteBackMemberImportFile,
  downloadAdminMemberImportTemplate,
  pickAdminMemberImportXlsxForWriteBack,
  writeAdminMemberImportResultToFileHandle,
  saveAdminMemberImportResultWithPicker,
  getAdminMemberAuthIdentities,
  getAdminMemberAuthIdentityLogs,
  assignAdminMemberTags,
  createAdminMemberTag,
  getAdminMemberTags,
  getAdminMembers,
  getAdminMemberMembershipApplySealFile,
  getFormFields,
  getForms,
  ensureMembershipApplyForMember,
  repairSupplementaryMembershipApplyDirty,
  getMembershipFeeSettings,
  postAdminMembersImport,
  approveAdminMemberOrderOfflinePay,
  rejectAdminMemberOrderOfflinePay,
  attachAdminMemberOrderVoucher,
  submitAdminMemberOrderOfflineVoucher,
  getAdminMemberRenewalSettings,
  previewAdminMemberRenewalSettings,
  submitAdminMemberRenewalSettings,
  ensureAdminMemberRenewalOrder,
  getAdminMemberPaymentRecords,
  uploadImage,
  unbindAdminMemberAuthIdentity,
  resetAdminMemberLocalPassword,
  unwrapAdminMembersResponse,
  updateAdminMember,
  revokeAdminMemberSessions,
  deleteAdminWithdrawnMember,
  getServiceConsultants,
} from '@/api'
import { hasAnyPermission, isSuperAdmin } from '@/utils/adminPermission'
import { loadApplyDutyOptionsFromMembershipForm } from '@/utils/membershipApplyDutyOptions'
import {
  ADMIN_MEMBER_LEVEL_OPTIONS,
  toMemberLevelSelectOptions,
} from '@/utils/adminMemberLevelOptions'
import { openNativeDatePicker } from '@/utils/nativeDatePicker'

const createEmpty = () => ({
  memberCode: '',
  nickname: '',
  avatarUrl: '',
  mobile: '',
  companyName: '',
  registerTitle: '',
  serviceConsultant: '',
  memberLevel: '',
  status: 'REGISTERED_IDLE',
  validUntil: '',
  validUntilManual: false,
  memberSort: 1,
  showInUnitList: true,
  showInNetworkList: true,
  withdrawn: false,
})

/** 同步 URL 筛选时避免 $route.query 监听重复触发 */
let skipRouteQueryWatch = false

export default {
  name: 'MemberManageView',
  components: { FancySelect, ListPager },
  data() {
    return {
      list: [],
      page: 1,
      pageSize: 10,
      keywordQ: '',
      unitQ: '',
      memberLevelQ: '',
      consultantQ: '',
      businessStatusQ: '',
      feeStatusQ: '',
      loginStatusQ: '',
      tagFilterQ: '',
      memberTags: [],
      selectedMemberIds: [],
      errorMsg: '',
      showDialog: false,
      showIdentityDialog: false,
      showResetPwdDialog: false,
      resetPwdBusy: false,
      resetPwdError: '',
      resetPwdForm: {
        memberId: null,
        name: '',
        mobile: '',
        password: '',
        password2: '',
      },
      editId: null,
      form: createEmpty(),
      identityMemberId: null,
      identityList: [],
      identityLogList: [],
      identityErrorMsg: '',
      identityRow: null,
      applyPreviewVisible: false,
      applyPreviewLoading: false,
      applyPreviewRow: null,
      applyPreviewItems: [],
      applyPreviewError: '',
      applyPreviewApplyRejectNote: '',
      applyPreviewFeeRejectNote: '',
      applyFieldDefs: [],
      applyGroupDefs: [],
      tagDlg: {
        visible: false,
        memberIds: [],
        selectedTagIds: [],
        replaceExisting: false,
        busy: false,
      },
      canEdit: false,
      isSuperAdmin: false,
      serviceConsultantOptions: [{ value: '', label: '（不指定）' }],
      expiringSoonDays: 10,
      statusOptions: [
        { value: 'ACTIVE', label: '正式会员' },
        { value: 'APPLY_REVIEW', label: '已申请待审核' },
        { value: 'REGISTERED_IDLE', label: '已注册未入会' },
        { value: 'WITHDRAW', label: '已退会' },
      ],
      businessStatusOptions: [
        { value: '', label: '全部业务状态' },
        { value: 'ACTIVE', label: '正式会员' },
        { value: 'APPLY_REVIEW', label: '已申请待审核' },
        { value: 'REGISTERED_IDLE', label: '已注册未入会' },
        { value: 'WITHDRAW', label: '已退会' },
      ],
      feeStatusOptions: [
        { value: '', label: '全部缴费状态' },
        { value: 'PENDING', label: '待缴费' },
        { value: 'REVIEWING', label: '待审核会费凭证' },
        { value: 'SOON', label: '即将到期' },
        { value: 'OVERDUE', label: '欠费' },
        { value: 'PAID', label: '已缴费' },
        { value: 'UNSET', label: '未出账' },
      ],
      loginStatusOptions: [
        { value: '', label: '全部登录状态' },
        { value: 'ONLINE', label: '在线' },
        { value: 'OFFLINE', label: '离线' },
      ],
      importSummary: '',
      importSummaryFail: false,
      repairDirtyBusy: false,
      /** 入会表单「申请职务」选项（来自表单管理 options_json） */
      applyDutyOptionsBase: [],
      applyDutyOptionsLoadError: '',
      feeVoucherDlg: {
        visible: false,
        row: null,
        meta: '',
        feeStatusLabel: '',
        img: '',
        canReview: false,
        canUpload: false,
        uploadOnly: false,
        viewOnly: false,
        pendingVoucherUrl: '',
        mockVoucher: false,
        rejectNote: '',
        joinPaymentMode: false,
        submissionId: 0,
        busy: false,
        errorMsg: '',
      },
      renewalDlg: {
        visible: false,
        row: null,
        loading: false,
        busy: false,
        errorMsg: '',
        successMessage: '',
        endDateAuto: true,
        pendingVoucherUrl: '',
        voucherPreview: '',
        levelOptions: [],
        form: {
          companyName: '',
          joinDate: '',
          validUntil: '',
          feeStatusLabel: '',
          currentMemberLevel: '',
          feeStandardText: '',
          amountYuan: '',
          renewalLevel: '',
          startDate: '',
          endDate: '',
          paymentMode: 'PENDING',
        },
      },
      paymentRecordsDlg: {
        visible: false,
        meta: '',
        loading: false,
        error: '',
        feeOrders: [],
        activityOrders: [],
      },
      highlightedMemberId: 0,
      needFillApplyToastVisible: true,
    }
  },
  watch: {
    needFillApplyMembers(list) {
      if (Array.isArray(list) && list.length > 0 && !this.isNeedFillApplyToastDismissed()) {
        this.needFillApplyToastVisible = true
      }
    },
    '$route.query': {
      deep: true,
      handler() {
        if (skipRouteQueryWatch) return
        this.applyFiltersFromRoute()
      },
    },
    totalCount(n) {
      this.page = clampPage(this.page, n, this.pageSize)
    },
    unitQ() {
      this.page = 1
    },
    memberLevelQ() {
      this.page = 1
    },
    consultantQ() {
      this.page = 1
    },
    businessStatusQ() {
      this.page = 1
    },
    loginStatusQ() {
      this.page = 1
    },
  },
  mounted() {
    this.refreshEditPermission()
    this.needFillApplyToastVisible = !this.isNeedFillApplyToastDismissed()
    this.applyFiltersFromRoute()
    this.loadFeeSettings()
    this.loadApplyDutyOptionsFromMembershipForm()
    this.loadServiceConsultants()
    this.loadMemberTags()
    this.load().then(() => this.scrollToHighlightedMember())
  },
  activated() {
    this.refreshEditPermission()
    this.applyFiltersFromRoute()
    this.loadServiceConsultants()
    this.load().then(() => this.scrollToHighlightedMember())
  },
  computed: {
    needFillApplyMembers() {
      return (this.list || []).filter((row) => {
        const flag = row?.needFillMembershipApply ?? row?.need_fill_membership_apply
        return !!flag
      })
    },
    needFillApplyMembersPreview() {
      return this.needFillApplyMembers.slice(0, 8)
    },
    validUntilEditable() {
      return String(this.form.status || '').toUpperCase() === 'ACTIVE'
    },
    memberCodeDisplay() {
      const code = String(this.form.memberCode || '').trim()
      return code || '成为正式会员后自动生成'
    },
    feeVoucherDialogTitle() {
      const d = this.feeVoucherDlg
      if (d.viewOnly) return '查看会费凭证'
      if (d.uploadOnly) return '帮他上传缴费凭证'
      return '审核会费凭证'
    },
    renewalAmountDisplay() {
      const v = String(this.renewalDlg.form.amountYuan || '').trim()
      return v ? `${v}元` : '—'
    },
    filteredList() {
      const arr = (this.list || []).filter((row) => {
        if (this.unitQ) {
          const v = String(row?.companyName || row?.registerCompanyName || '')
          if (!v.includes(this.unitQ)) return false
        }
        if (this.memberLevelQ) {
          const v = String(row?.memberLevel || '').trim()
          if (v !== String(this.memberLevelQ).trim()) return false
        }
        if (this.consultantQ) {
          const v = String(row?.serviceConsultant || '').trim()
          if (v !== String(this.consultantQ).trim()) return false
        }

        if (this.businessStatusQ) {
          const status = String(row?.status || '').toUpperCase()
          const remark = String(row?.remark || '')
          const isApplyReview = status !== 'ACTIVE' && remark.includes('入会申请')
          const isRegisteredIdle = status !== 'ACTIVE' && !remark.includes('入会申请')

          if (this.businessStatusQ === 'ACTIVE' && status !== 'ACTIVE') return false
          if (this.businessStatusQ === 'WITHDRAW' && status !== 'WITHDRAW') return false
          if (this.businessStatusQ === 'APPLY_REVIEW' && !isApplyReview) return false
          if (this.businessStatusQ === 'REGISTERED_IDLE' && !isRegisteredIdle) return false
        }

        if (this.feeStatusQ) {
          const v = String(row?.feeStatus || 'UNSET').toUpperCase()
          if (v !== String(this.feeStatusQ).toUpperCase()) return false
        }

        if (this.loginStatusQ) {
          const online = Boolean(row?.online)
          if (this.loginStatusQ === 'ONLINE' && !online) return false
          if (this.loginStatusQ === 'OFFLINE' && online) return false
        }

        return true
      })

      return arr
    },
    sortedList() {
      return (this.filteredList || [])
        .slice()
        .sort((a, b) => {
          const sa = Number(a.memberSort || 1)
          const sb = Number(b.memberSort || 1)
          if (sa !== sb) return sa - sb
          return Number(b.id || 0) - Number(a.id || 0)
        })
    },
    totalCount() {
      return (this.sortedList || []).length
    },
    rowOffset() {
      return pageRowOffset(this.page, this.pageSize)
    },
    paginatedList() {
      return slicePageRows(this.sortedList, this.page, this.pageSize)
    },
    allChecked() {
      return this.paginatedList.length > 0 && this.paginatedList.every((row) => this.selectedMemberIds.includes(row.id))
    },
    consultantFilterOptions() {
      const opts = [{ value: '', label: '全部服务专员' }]
      for (const row of this.serviceConsultantOptions || []) {
        const value = String(row?.value || '').trim()
        if (!value) continue
        opts.push({ value, label: String(row.label || value).trim() || value })
      }
      return opts
    },
    memberTagFilterOptions() {
      const opts = [{ value: '', label: '全部标签' }]
      for (const tag of this.memberTags || []) {
        opts.push({ value: String(tag.id), label: tag.tagName })
      }
      return opts
    },
    memberLevelFilterOptions() {
      const opts = [{ value: '', label: '全部会员级别' }]
      const seen = new Set([''])
      for (const row of toMemberLevelSelectOptions(ADMIN_MEMBER_LEVEL_OPTIONS)) {
        const value = String(row?.value || '').trim()
        if (!value || seen.has(value)) continue
        seen.add(value)
        opts.push({ value, label: value })
      }
      for (const row of this.list || []) {
        const value = String(row?.memberLevel || '').trim()
        if (!value || seen.has(value)) continue
        seen.add(value)
        opts.push({ value, label: `${value}（列表中）` })
      }
      return opts
    },
    /** 后台完整会员级别 + 当前值兜底（申请表仅 5 项，此处含会长/监事长等） */
    memberLevelDutyOptions() {
      const empty = { value: '', label: '— 不填 —' }
      const base = toMemberLevelSelectOptions(ADMIN_MEMBER_LEVEL_OPTIONS)
      const ml = String(this.form?.memberLevel || '').trim()
      const seen = new Set(base.map((o) => String(o.value)))
      const tail = []
      if (ml && !seen.has(ml)) {
        tail.push({ value: ml, label: `${ml}（当前值）` })
      }
      return [empty, ...base, ...tail]
    },
  },
  methods: {
    openNativeDatePicker,
    registerNameText(row) {
      const v = row?.registerName ?? row?.nickname
      const s = v == null ? '' : String(v).trim()
      return s || '-'
    },
    registerMobileText(row) {
      const v = row?.registerMobile ?? row?.mobile
      const s = v == null ? '' : String(v).trim()
      return s || '-'
    },
    registerTitleText(row) {
      const v = row?.registerTitle
      const s = v == null ? '' : String(v).trim()
      return s || '-'
    },
    /** 入会申请表「会员代表信息」姓名；后端从最新/已通过申请 payload 解析 repName */
    representativeNameText(row) {
      const v = row?.representativeName
      const s = v == null ? '' : String(v).trim()
      return s || '-'
    },
    representativeMobileText(row) {
      const v = row?.representativeMobile
      const s = v == null ? '' : String(v).trim()
      return s || '-'
    },
    mapCoordText(row) {
      const lat = row?.mapLatitude
      const lng = row?.mapLongitude
      if (lat == null || lng == null) return '-'
      return `${lat}, ${lng}`
    },
    businessStatusText(row) {
      const status = String(row?.status || '').toUpperCase()
      if (status === 'WITHDRAW' || row?.withdrawn) return '已退会'
      if (status === 'ACTIVE') return '正式会员'
      const remark = String(row?.remark || '')
      if (remark.includes('入会申请')) return '已申请待审核'
      return '已注册未入会'
    },
    businessStatusClass(row) {
      const status = String(row?.status || '').toUpperCase()
      if (status === 'WITHDRAW' || row?.withdrawn) return 'status-pill--idle'
      if (status === 'ACTIVE') return 'status-pill--active'
      const remark = String(row?.remark || '')
      if (remark.includes('入会申请')) return 'status-pill--review'
      return 'status-pill--idle'
    },
    /** 编辑弹窗状态下拉：与列表业务状态四态对齐 */
    resolveFormBusinessStatus(row) {
      const status = String(row?.status || '').toUpperCase()
      if (status === 'WITHDRAW' || row?.withdrawn) return 'WITHDRAW'
      if (status === 'ACTIVE') return 'ACTIVE'
      const remark = String(row?.remark || '')
      if (remark.includes('入会申请')) return 'APPLY_REVIEW'
      return 'REGISTERED_IDLE'
    },
    feeStatusClass(status) {
      const s = String(status || '').toUpperCase()
      if (s === 'PAID') return 'status-pill--active'
      if (s === 'PENDING') return 'status-pill--pay'
      if (s === 'REVIEWING') return 'status-pill--review'
      if (s === 'SOON') return 'status-pill--review'
      if (s === 'OVERDUE') return 'status-pill--idle'
      if (s === 'CANCELLED') return 'status-pill--idle'
      return 'status-pill--biz'
    },
    /** 非正式会员无缴费态时显示 —；「未出账」仅正式会员续费账单未出 */
    displayFeeStatusLabel(row) {
      const label = String(row?.feeStatusLabel || '').trim()
      if (label) return label
      const biz = this.resolveBusinessStatus(row)
      if (biz !== 'ACTIVE') return '—'
      return '未出账'
    },
    businessStatusHint(row) {
      const p = String(row?.membershipPipelineLabel || '').trim()
      if (p === '申请表被驳回') return p
      return ''
    },
    feeStatusHint(row) {
      const joinHint = String(row?.joinPaymentHint || '').trim()
      if (joinHint) return joinHint
      const renewalHint = String(row?.renewalFeeHint || row?.renewal_fee_hint || '').trim()
      const feeRejectNote = String(row?.membershipFeeReviewNote || row?.membership_fee_review_note || '').trim()
      if (feeRejectNote && renewalHint) return feeRejectNote
      if (renewalHint) return renewalHint
      const pipeline = String(row?.membershipPipelineLabel || '').trim()
      if (pipeline === '入会缴费被驳回') return pipeline
      const pay = String(row?.feePayMethod || row?.fee_pay_method || '').trim().toUpperCase()
      if (pay === 'ONLINE' && String(row?.feeStatus || '').toUpperCase() === 'REVIEWING') {
        return '已线上缴费，请补录到账凭证'
      }
      return ''
    },
    memberRowShowUploadFeeVoucher(row) {
      return !!row?.canUploadFeeVoucher
    },
    memberRowShowReviewFeeVoucher(row) {
      return !!row?.canReviewFeeVoucher
    },
    memberRowShowViewFeeVoucher(row) {
      return !!row?.canViewFeeVoucher && !row?.canReviewFeeVoucher
    },
    memberRowShowEnsureRenewal(row) {
      return !!row?.canEnsureRenewal
    },
    /** 会费凭证：待审核时可审核；已缴费后可查看凭证 */
    memberRowShowFeeVoucher(row) {
      return this.memberRowShowReviewFeeVoucher(row) || this.memberRowShowViewFeeVoucher(row)
    },
    feeYuanFromCent(cent) {
      const n = Number(cent)
      if (!Number.isFinite(n) || n <= 0) return null
      return (n / 100).toFixed(2)
    },
    /**
     * 会费列展示：
     * - 待缴费 / 待审凭证：优先订单金额（当前应付）
     * - 即将到期 / 欠费 / 已缴费 / 未出账：优先级别定价（与会费定价表对应）
     */
    feeMoneyText(row) {
      const yOrder = this.feeYuanFromCent(row?.feeOrderAmountCent)
      const yRef = this.feeYuanFromCent(row?.feePricingAmountCent)
      const st = String(row?.feeStatus || 'UNSET').toUpperCase()
      const payingNow = st === 'PENDING' || st === 'REVIEWING'
      if (payingNow) {
        if (yOrder) return yOrder
        if (yRef) return `${yRef}（定价）`
        return '—'
      }
      if (yRef) return yRef
      if (yOrder) return yOrder
      return '—'
    },
    feeMoneyMissingPricing(row) {
      const yRef = this.feeYuanFromCent(row?.feePricingAmountCent)
      if (yRef) return false
      const level = String(row?.memberLevel || '').trim()
      if (!level) return false
      const st = String(row?.feeStatus || 'UNSET').toUpperCase()
      const payingNow = st === 'PENDING' || st === 'REVIEWING'
      // 待缴场景有订单金额时仍显示订单，不强制「未维护」
      if (payingNow && this.feeYuanFromCent(row?.feeOrderAmountCent)) return false
      return true
    },
    feeMoneyTitle(row) {
      const yo = this.feeYuanFromCent(row?.feeOrderAmountCent)
      const yp = this.feeYuanFromCent(row?.feePricingAmountCent)
      const st = String(row?.feeStatus || 'UNSET').toUpperCase()
      if (this.feeMoneyMissingPricing(row)) {
        return '该会员级别在会费定价中未维护，请到：会员 → 模板管理 → 会费定价'
      }
      if (yo && yp && yo !== yp) {
        if (st === 'PENDING' || st === 'REVIEWING') {
          return `当前应付（订单）¥${yo}；级别定价 ¥${yp}`
        }
        return `级别定价 ¥${yp}；历史订单金额 ¥${yo}`
      }
      if (yp) return `按会员级别「会费定价」¥${yp}`
      if (yo) return `会费订单 ¥${yo}`
      return ''
    },
    goFeePricingMaintain() {
      this.$router.push({ path: '/member/templates', query: { tab: 'price' } }).catch(() => {})
    },
    formatDateTime(v) {
      const text = String(v || '').trim()
      if (!text) return '-'
      return text.replace('T', ' ')
    },
    registerCompanyText(row) {
      const v = row?.registerCompanyName
      const s = v == null ? '' : String(v).trim()
      return s || '-'
    },
    membershipPayloadUnit(payload) {
      const p = payload && typeof payload === 'object' ? payload : {}
      for (const k of ['companyName', 'applicantUnit', 'unitName']) {
        const s = String(p[k] || '').trim()
        if (s) return s
      }
      return ''
    },
    async viewMembershipApply(row) {
      const memberId = Number(row?.id || 0)
      if (!memberId) {
        this.errorMsg = '会员无效'
        return
      }
      this.errorMsg = ''
      try {
        let id = Number(row?.membershipApplySubmissionId || 0)
        if (!id) {
          const ensured = await ensureMembershipApplyForMember(memberId)
          id = Number(ensured?.id || 0)
          if (id) {
            row.membershipApplySubmissionId = id
          }
        }
        if (!id) {
          this.errorMsg = '无法打开申请表'
          return
        }
        await this.$router.push({
          path: '/form/submissions',
          query: {
            formCode: 'membership_apply',
            highlightSubmissionId: String(id),
            openSubmissionId: String(id),
            openEdit: '1',
            returnTo: '/member/manage',
          },
        })
      } catch (e) {
        this.errorMsg = e?.message || '打开申请表失败'
      }
    },
    closeApplyPreview() {
      this.applyPreviewVisible = false
      this.applyPreviewLoading = false
      this.applyPreviewRow = null
      this.applyPreviewItems = []
      this.applyPreviewError = ''
      this.applyPreviewApplyRejectNote = ''
      this.applyPreviewFeeRejectNote = ''
    },
    goApplyRecordsForJoinFee(row) {
      const id = Number(row?.membershipApplySubmissionId || 0)
      if (!id) {
        this.errorMsg = '未找到入会申请记录'
        return
      }
      this.$router.push({
        path: '/form/submissions',
        query: {
          formCode: 'membership_apply',
          highlightSubmissionId: String(id),
          returnTo: '/member/manage',
        },
      })
    },
    async onUploadFeeVoucher(row) {
      if (!row) return
      if (row.joinFeeOnApplyRecords) {
        this.goApplyRecordsForJoinFee(row)
        return
      }
      let target = row
      const feeSt = String(row?.feeStatus || '').toUpperCase()
      // 即将到期/欠费通常还没有待缴续费单：先生成再打开上传
      if (feeSt === 'SOON' || feeSt === 'OVERDUE') {
        if (!this.canEdit) {
          this.errorMsg = '当前账号无会员编辑权限'
          return
        }
        try {
          this.errorMsg = ''
          const res = await ensureAdminMemberRenewalOrder(row.id)
          const newOrderId = Number(res?.orderId || res?.order_id || 0)
          if (!newOrderId) throw new Error(res?.message || '续费单生成失败')
          await this.load()
          target = (this.rows || []).find((r) => Number(r.id) === Number(row.id)) || {
            ...row,
            feeOrderId: newOrderId,
            feeStatus: 'PENDING',
            feeStatusLabel: '待缴费',
            canUploadFeeVoucher: true,
          }
          if (!Number(target.feeOrderId || 0)) {
            target = { ...target, feeOrderId: newOrderId, canUploadFeeVoucher: true }
          }
        } catch (e) {
          this.errorMsg = e.message || '生成续费单失败，无法上传凭证'
          return
        }
      }
      this.openFeeVoucherAudit(target, { uploadOnly: true })
    },
    onReviewFeeVoucher(row) {
      if (!row) return
      if (row.joinFeeOnApplyRecords) {
        this.goApplyRecordsForJoinFee(row)
        return
      }
      this.openFeeVoucherAudit(row, { review: true })
    },
    onViewFeeVoucher(row) {
      if (!row) return
      if (row.joinFeeOnApplyRecords) {
        this.goApplyRecordsForJoinFee(row)
        return
      }
      this.openFeeVoucherAudit(row, { viewOnly: true })
    },
    onEnsureRenewal(row) {
      if (!this.canEdit) {
        this.errorMsg = '当前账号无会员编辑权限'
        return
      }
      if (!row?.id) return
      this.renewalDlg = {
        visible: true,
        row,
        loading: true,
        busy: false,
        errorMsg: '',
        successMessage: '',
        endDateAuto: true,
        pendingVoucherUrl: '',
        voucherPreview: '',
        levelOptions: [],
        form: {
          companyName: row.companyName || row.representativeName || '',
          joinDate: '',
          validUntil: '',
          feeStatusLabel: '',
          currentMemberLevel: '',
          feeStandardText: '',
          amountYuan: '',
          renewalLevel: '',
          startDate: '',
          endDate: '',
          paymentMode: 'PENDING',
        },
      }
      this.loadRenewalSettingsForm(row.id)
    },
    async loadRenewalSettingsForm(memberId) {
      try {
        const data = await getAdminMemberRenewalSettings(memberId)
        const f = this.renewalDlg.form
        f.companyName = data.companyName || f.companyName
        f.joinDate = data.joinDate || ''
        f.validUntil = data.validUntil || ''
        f.feeStatusLabel = data.feeStatusLabel || ''
        f.currentMemberLevel = data.currentMemberLevel || ''
        f.feeStandardText = data.feeStandardText || ''
        f.amountYuan = data.amountYuan != null ? String(data.amountYuan) : ''
        f.renewalLevel = data.renewalLevel || data.currentMemberLevel || ''
        f.startDate = data.startDate || ''
        f.endDate = data.endDate || ''
        f.paymentMode = data.paymentMode === 'PAID' ? 'PAID' : 'PENDING'
        this.renewalDlg.levelOptions = Array.isArray(data.levelOptions) && data.levelOptions.length
          ? data.levelOptions
          : [f.renewalLevel].filter(Boolean)
        this.renewalDlg.endDateAuto = data.endDateAuto !== false
      } catch (e) {
        this.renewalDlg.errorMsg = e.message || '加载续费设置失败'
      } finally {
        this.renewalDlg.loading = false
      }
    },
    async onRenewalPreviewFieldsChange() {
      const row = this.renewalDlg.row
      if (!row?.id) return
      try {
        const data = await previewAdminMemberRenewalSettings(
          row.id,
          this.renewalDlg.form.renewalLevel,
          this.renewalDlg.form.startDate,
        )
        if (data.feeStandardText) this.renewalDlg.form.feeStandardText = data.feeStandardText
        if (data.amountYuan != null) this.renewalDlg.form.amountYuan = String(data.amountYuan)
        if (this.renewalDlg.endDateAuto && data.endDate) this.renewalDlg.form.endDate = data.endDate
      } catch {
        /* ignore */
      }
    },
    onRenewalEndDateManual() {
      this.renewalDlg.endDateAuto = false
    },
    pickRenewalVoucherFile() {
      if (this.renewalDlg.busy) return
      const el = this.$refs.renewalVoucherFileInput
      if (el) el.click()
    },
    async onRenewalVoucherFilePicked(e) {
      const file = e && e.target && e.target.files && e.target.files[0]
      if (e && e.target) e.target.value = ''
      if (!file) return
      this.renewalDlg.busy = true
      this.renewalDlg.errorMsg = ''
      try {
        const data = await uploadImage(file)
        const url = String((data && data.url) || '').trim()
        if (!url) throw new Error('未获取到图片地址')
        this.renewalDlg.pendingVoucherUrl = url
        this.renewalDlg.voucherPreview = this.feeVoucherAbs(url)
      } catch (err) {
        this.renewalDlg.errorMsg = err.message || '上传凭证失败'
      } finally {
        this.renewalDlg.busy = false
      }
    },
    closeRenewalConfirm() {
      this.renewalDlg = {
        visible: false,
        row: null,
        loading: false,
        busy: false,
        errorMsg: '',
        successMessage: '',
        endDateAuto: true,
        pendingVoucherUrl: '',
        voucherPreview: '',
        levelOptions: [],
        form: {
          companyName: '',
          joinDate: '',
          validUntil: '',
          feeStatusLabel: '',
          currentMemberLevel: '',
          feeStandardText: '',
          amountYuan: '',
          renewalLevel: '',
          startDate: '',
          endDate: '',
          paymentMode: 'PENDING',
        },
      }
    },
    async openPaymentRecords(row) {
      if (!row || row.id == null) return
      const name = row.companyName || row.registerCompanyName || row.nickname || row.registerName || '—'
      this.paymentRecordsDlg = {
        visible: true,
        meta: `${name} · 会员 #${row.id}`,
        loading: true,
        error: '',
        feeOrders: [],
        activityOrders: [],
      }
      try {
        const data = await getAdminMemberPaymentRecords(row.id)
        const feeOrders = (data && data.feeOrders) || (data && data.fee_orders) || []
        const activityOrders = (data && data.activityOrders) || (data && data.activity_orders) || []
        this.paymentRecordsDlg.feeOrders = Array.isArray(feeOrders) ? feeOrders : []
        this.paymentRecordsDlg.activityOrders = Array.isArray(activityOrders) ? activityOrders : []
      } catch (e) {
        const msg = String((e && e.message) || '')
        this.paymentRecordsDlg.error =
          /404|not found/i.test(msg)
            ? '接口未找到，请重启 Java 后端（huiyuanguanlihouduan1）后重试'
            : msg || '加载缴费记录失败'
      } finally {
        this.paymentRecordsDlg.loading = false
      }
    },
    closePaymentRecords() {
      this.paymentRecordsDlg = {
        visible: false,
        meta: '',
        loading: false,
        error: '',
        feeOrders: [],
        activityOrders: [],
      }
    },
    async submitRenewalConfirm() {
      const row = this.renewalDlg.row
      if (!row?.id || this.renewalDlg.busy || this.renewalDlg.loading || this.renewalDlg.successMessage) return
      const f = this.renewalDlg.form
      if (!f.renewalLevel) {
        this.renewalDlg.errorMsg = '请选择续期职务'
        return
      }
      if (!f.startDate || !f.endDate) {
        this.renewalDlg.errorMsg = '请填写续费起止时间'
        return
      }
      if (f.paymentMode === 'PAID' && !this.renewalDlg.pendingVoucherUrl) {
        this.renewalDlg.errorMsg = '设为已缴费须上传凭证'
        return
      }
      this.renewalDlg.busy = true
      this.renewalDlg.errorMsg = ''
      this.errorMsg = ''
      try {
        const res = await submitAdminMemberRenewalSettings(row.id, {
          renewalLevel: f.renewalLevel,
          startDate: f.startDate,
          endDate: f.endDate,
          paymentMode: f.paymentMode,
          voucherUrl: this.renewalDlg.pendingVoucherUrl || '',
        })
        await this.load()
        this.renewalDlg.successMessage = (res && res.message) || '续费设置已保存'
      } catch (e) {
        this.renewalDlg.errorMsg = e.message || '续费设置失败'
      } finally {
        this.renewalDlg.busy = false
      }
    },
    openApplyPreviewLink(url) {
      const u = String(url || '').trim()
      if (!u) return
      window.open(u, '_blank', 'noopener')
    },
    async loadFeeSettings() {
      try {
        const row = await getMembershipFeeSettings()
        const n = Number(row && row.expiringSoonDays)
        if (Number.isFinite(n) && n > 0) this.expiringSoonDays = n
      } catch {
        /* 使用默认 10 */
      }
    },
    async loadApplyDutyOptionsFromMembershipForm() {
      const { base, error } = await loadApplyDutyOptionsFromMembershipForm(getForms, getFormFields)
      this.applyDutyOptionsBase = base
      this.applyDutyOptionsLoadError = error || ''
    },
    async loadServiceConsultants() {
      try {
        const rows = (await getServiceConsultants()) || []
        const opts = [{ value: '', label: '（不指定）' }]
        for (const row of rows) {
          const name = String(row?.displayName || '').trim()
          if (!name) continue
          opts.push({ value: name, label: name })
        }
        this.serviceConsultantOptions = opts
      } catch {
        this.serviceConsultantOptions = [{ value: '', label: '（不指定）' }]
      }
    },
    async loadMemberTags() {
      try {
        const rows = (await getAdminMemberTags()) || []
        this.memberTags = Array.isArray(rows) ? rows : []
      } catch {
        this.memberTags = []
      }
    },
    memberTagNames(row) {
      const names = row?.tagNames
      if (!Array.isArray(names)) return []
      return names.map((x) => String(x).trim()).filter(Boolean)
    },
    openTagDialog(row) {
      const ids = row ? [row.id] : [...this.selectedMemberIds]
      if (!ids.length) {
        this.errorMsg = '请勾选要操作的数据'
        return
      }
      let selected = []
      if (row && Array.isArray(row.tagNames) && row.tagNames.length) {
        const names = new Set(row.tagNames.map((x) => String(x).trim()))
        selected = (this.memberTags || []).filter((t) => names.has(String(t.tagName).trim())).map((t) => t.id)
      }
      this.tagDlg = {
        visible: true,
        memberIds: ids,
        selectedTagIds: selected,
        replaceExisting: !!row,
        busy: false,
      }
    },
    closeTagDialog() {
      this.tagDlg.visible = false
      this.tagDlg.busy = false
    },
    async promptNewTag() {
      const name = await adminPrompt('请输入新标签名称（不可重复）')
      if (!name) return
      try {
        this.tagDlg.busy = true
        const created = await createAdminMemberTag(String(name).trim())
        await this.loadMemberTags()
        if (created && created.id) {
          if (!this.tagDlg.selectedTagIds.includes(created.id)) {
            this.tagDlg.selectedTagIds = [...this.tagDlg.selectedTagIds, created.id]
          }
        }
      } catch (e) {
        this.errorMsg = e.message || '新增标签失败'
      } finally {
        this.tagDlg.busy = false
      }
    },
    async submitTagDialog() {
      if (!this.tagDlg.selectedTagIds.length) return
      try {
        this.tagDlg.busy = true
        await assignAdminMemberTags({
          memberIds: this.tagDlg.memberIds,
          tagIds: this.tagDlg.selectedTagIds.map((id) => Number(id)),
          replaceExisting: this.tagDlg.replaceExisting,
        })
        this.closeTagDialog()
        await this.load()
      } catch (e) {
        this.errorMsg = e.message || '打标签失败'
      } finally {
        this.tagDlg.busy = false
      }
    },
    async load() {
      try {
        this.errorMsg = ''
        const params = {}
        const kw = String(this.keywordQ || '').trim()
        if (kw) params.keyword = kw
        const tagId = Number(this.tagFilterQ || 0)
        if (tagId > 0) params.tagId = tagId
        const raw = (await getAdminMembers(params)) || []
        this.list = unwrapAdminMembersResponse(raw)
        const ids = new Set(this.list.map((x) => x.id))
        this.selectedMemberIds = this.selectedMemberIds.filter((id) => ids.has(id))
        if (this.needFillApplyMembers.length && !this.isNeedFillApplyToastDismissed()) {
          this.needFillApplyToastVisible = true
        }
      } catch (e) {
        this.errorMsg = e.message || '读取会员失败'
      }
    },
    isNeedFillApplyToastDismissed() {
      try {
        return sessionStorage.getItem('member.needFillApply.dismissed') === '1'
      } catch (_) {
        return false
      }
    },
    dismissNeedFillApplyToast() {
      this.needFillApplyToastVisible = false
      try {
        sessionStorage.setItem('member.needFillApply.dismissed', '1')
      } catch (_) {
        /* ignore */
      }
    },
    needFillApplyMemberLabel(row) {
      const name = String(row?.nickname || row?.registerName || '').trim()
      const unit = String(row?.registerCompanyName || row?.companyName || '').trim()
      const mobile = String(row?.mobile || '').trim()
      const parts = [name || `会员#${row?.id || ''}`]
      if (unit) parts.push(unit)
      if (mobile) parts.push(mobile)
      return parts.join(' · ')
    },
    async openNeedFillApplyMember(row) {
      if (!row) return
      this.highlightedMemberId = Number(row.id || 0)
      try {
        await this.viewMembershipApply(row)
      } catch (_) {
        /* viewMembershipApply 已处理错误 */
      }
    },
    search() {
      this.page = 1
      this.load()
    },
    applyFiltersFromRoute(route = this.$route) {
      const q = route?.query || {}
      if (q.keyword != null) this.keywordQ = String(q.keyword)
      this.feeStatusQ = q.feeStatus != null ? String(q.feeStatus) : ''
    },
    async scrollToHighlightedMember() {
      const memberId = Number(this.$route?.query?.highlightMemberId || 0)
      const orderId = Number(this.$route?.query?.highlightOrderId || 0)
      const openSeal = String(this.$route?.query?.openSeal || '').trim() === '1'
      if (!memberId && !orderId && !openSeal) return
      let row = null
      if (memberId > 0) {
        row = (this.sortedList || []).find((r) => Number(r.id) === memberId)
      }
      if (!row && orderId > 0) {
        row = (this.sortedList || []).find((r) => Number(r.feeOrderId || 0) === orderId)
      }
      if (!row) {
        if (openSeal || memberId || orderId) {
          await this.removeMemberHighlightFromRoute()
        }
        return
      }
      this.highlightedMemberId = Number(row.id)
      const idx = (this.sortedList || []).findIndex((r) => Number(r.id) === Number(row.id))
      if (idx >= 0) {
        const size = Math.max(1, Number(this.pageSize) || 10)
        this.page = Math.floor(idx / size) + 1
      }
      const shouldOpenSeal = openSeal
      await this.removeMemberHighlightFromRoute()
      this.$nextTick(() => {
        const ref = this.$refs[`memberRow-${row.id}`]
        const el = Array.isArray(ref) ? ref[0] : ref
        if (el && typeof el.scrollIntoView === 'function') {
          el.scrollIntoView({ block: 'center', behavior: 'smooth' })
        }
        if (shouldOpenSeal) {
          void this.viewMembershipApplySealFile(row)
        }
      })
    },
    clearMemberHighlight() {
      this.highlightedMemberId = 0
    },
    async removeMemberHighlightFromRoute() {
      const q = this.$route?.query || {}
      if (!q.highlightMemberId && !q.highlightOrderId && !q.openSeal && !q.highlightSubmissionId) return
      const next = { ...q }
      delete next.highlightMemberId
      delete next.highlightOrderId
      delete next.openSeal
      delete next.highlightSubmissionId
      skipRouteQueryWatch = true
      try {
        const resolved = this.$router.resolve({ path: this.$route.path, query: next })
        if (resolved.fullPath !== this.$route.fullPath) {
          await this.$router.replace({ path: this.$route.path, query: next })
        }
      } catch {
        /* ignore */
      } finally {
        this.$nextTick(() => {
          skipRouteQueryWatch = false
        })
      }
    },
    buildFilterQuery() {
      const q = { ...(this.$route?.query || {}) }
      const kw = String(this.keywordQ || '').trim()
      const fs = String(this.feeStatusQ || '').trim()
      if (kw) q.keyword = kw
      else delete q.keyword
      delete q.status
      if (fs) q.feeStatus = fs
      else delete q.feeStatus
      return q
    },
    async pushFiltersToRoute() {
      const query = this.buildFilterQuery()
      const resolved = this.$router.resolve({ path: this.$route.path, query })
      if (resolved.fullPath === this.$route.fullPath) return
      skipRouteQueryWatch = true
      try {
        await this.$router.replace({ path: this.$route.path, query })
      } catch {
        /* ignore */
      } finally {
        this.$nextTick(() => {
          skipRouteQueryWatch = false
        })
      }
    },
    async onFeeStatusChange() {
      this.page = 1
      skipRouteQueryWatch = true
      try {
        await this.pushFiltersToRoute()
      } finally {
        this.$nextTick(() => {
          skipRouteQueryWatch = false
        })
      }
    },
    async resetFilters() {
      this.page = 1
      this.keywordQ = ''
      this.unitQ = ''
      this.memberLevelQ = ''
      this.consultantQ = ''
      this.businessStatusQ = ''
      this.feeStatusQ = ''
      this.loginStatusQ = ''
      this.tagFilterQ = ''
      this.importSummary = ''
      this.importSummaryFail = false
      skipRouteQueryWatch = true
      try {
        await this.pushFiltersToRoute()
      } finally {
        this.$nextTick(() => {
          skipRouteQueryWatch = false
        })
      }
      this.load()
    },
    refreshEditPermission() {
      // 导入/下载模板/新增编辑：须「会员管理-编辑」（旧版 member.manage 仍兼容）
      this.canEdit = hasAnyPermission(['member.edit', 'member.manage'])
      this.isSuperAdmin = isSuperAdmin()
    },
    async downloadImportTemplate() {
      this.refreshEditPermission()
      if (!this.canEdit) {
        this.errorMsg = '权限不足：下载导入模板需要「会员管理-编辑」权限'
        return
      }
      this.errorMsg = ''
      try {
        await downloadAdminMemberImportTemplate()
      } catch (e) {
        const msg = e.message || '下载模板失败'
        this.errorMsg = msg
        try {
          window.alert(msg)
        } catch (_) {
          /* ignore */
        }
      }
    },
    async clickImportFile() {
      this.refreshEditPermission()
      if (!this.canEdit) {
        this.errorMsg = '权限不足：Excel 导入需要「会员管理-编辑」权限'
        return
      }
      // Chrome/Edge：选文件后写回原路径；其它浏览器退回 input 上传 + 同名下载
      if (canWriteBackMemberImportFile()) {
        try {
          const picked = await pickAdminMemberImportXlsxForWriteBack()
          await this.runMemberImport(picked.file, { fileHandle: picked.handle })
          return
        } catch (e) {
          // 用户取消选择
          if (e && (e.name === 'AbortError' || /cancel|取消/i.test(String(e.message || '')))) {
            return
          }
          // 无权限 / 不支持写回时退回普通选择
          if (e && /写入权限|不支持写回|无法写回/.test(String(e.message || ''))) {
            this.$refs.importFileInput?.click()
            return
          }
          this.importSummaryFail = true
          this.importSummary = e?.message || '选择文件失败'
          return
        }
      }
      this.$refs.importFileInput?.click()
    },
    async repairSupplementaryDirty() {
      if (!this.canEdit || this.repairDirtyBusy) return
      const ok = await adminConfirm(
        '将扫描并修复：正式会员补申请表后被误降为「已申请待审核」、申请职务与主档不一致、误生成的待付入会首次会费。是否继续？',
      )
      if (!ok) return
      this.repairDirtyBusy = true
      this.errorMsg = ''
      try {
        const r = await repairSupplementaryMembershipApplyDirty()
        const restored = Number(r?.restoredActive ?? 0)
        const duty = Number(r?.dutySynced ?? 0)
        const fees = Number(r?.feesVoided ?? 0)
        const scanned = Number(r?.scanned ?? 0)
        const msg =
          `修复完成：扫描 ${scanned} 人，恢复正式会员 ${restored}，同步职务 ${duty}，作废误会费单 ${fees}。`
        this.importSummary = msg
        try {
          window.alert(msg)
        } catch (_) {
          /* ignore */
        }
        await this.load()
      } catch (e) {
        const msg = e?.message || '修复失败'
        this.errorMsg = msg
        try {
          window.alert(msg)
        } catch (_) {
          /* ignore */
        }
      } finally {
        this.repairDirtyBusy = false
      }
    },
    async onImportFile(ev) {
      const input = ev.target
      const file = input && input.files && input.files[0]
      if (input) input.value = ''
      if (!file || !this.canEdit) return
      await this.runMemberImport(file, { fileHandle: null })
    },
    /**
     * @param {File} file
     * @param {{ fileHandle?: FileSystemFileHandle | null }} [opts]
     */
    async runMemberImport(file, opts = {}) {
      if (!file || !this.canEdit) return
      this.errorMsg = ''
      this.importSummary = ''
      this.importSummaryFail = false
      const name = String(file.name || '').toLowerCase()
      if (!name.endsWith('.xlsx')) {
        this.importSummaryFail = true
        this.importSummary = '仅支持 .xlsx 格式文件，请下载最新模板后填写'
        return
      }
      const fileHandle = opts.fileHandle || null
      const originalName = String(file.name || 'member_import.xlsx').trim() || 'member_import.xlsx'
      try {
        const r = await postAdminMembersImport(file)
        const c = Number(r.created ?? 0)
        const s = Number(r.skipped ?? 0)
        const fails = Array.isArray(r.failures) ? r.failures : []
        const hasFail = fails.length > 0
        let msg = hasFail ? `导入未全部成功：新建 ${c} 条` : `导入完成：新建 ${c} 条`
        if (s) msg += `，跳过空行 ${s} 条`
        if (hasFail) msg += `，失败 ${fails.length} 条`
        msg += '。'
        const b64 = r.errorFileBase64 || r.error_file_base64 || r.resultFileBase64
        if (b64) {
          let wroteBack = false
          if (fileHandle) {
            const tryWrite = async () => {
              await writeAdminMemberImportResultToFileHandle(fileHandle, b64)
            }
            try {
              await tryWrite()
              wroteBack = true
            } catch (writeErr) {
              const reason = writeErr?.message || '可能正被 Excel 打开'
              const retry = window.confirm(
                `无法直接写回原文件（${reason}）。\n\n请先关闭 Excel 中打开的该文件，然后点「确定」再写回一次。\n点「取消」则自行选择保存位置覆盖原文件。`,
              )
              if (retry) {
                try {
                  await tryWrite()
                  wroteBack = true
                } catch (e2) {
                  /* fall through to picker */
                }
              }
              if (!wroteBack) {
                try {
                  const mode = await saveAdminMemberImportResultWithPicker(b64, originalName)
                  if (mode === 'saved') {
                    wroteBack = true
                    msg += hasFail
                      ? ` 请用 Excel 打开刚保存的文件，查看第 1 列「导入结果」中的失败原因。`
                      : ` 请用 Excel 打开刚保存的文件，查看第 1 列「导入结果」。`
                  } else {
                    msg += ' 已取消保存结果文件；导入数据已生效，可稍后重新导入同一文件以回写结果列。'
                  }
                } catch (_) {
                  msg += ' 写回失败，请关闭 Excel 后重试导入。'
                }
              }
            }
            if (wroteBack && !/请用 Excel 打开/.test(msg)) {
              msg += hasFail
                ? ` 已写回原文件「${originalName}」第 1 列。请用 Excel 打开该文件查看失败原因（勿另存为新副本）。`
                : ` 已写回原文件「${originalName}」第 1 列。请用 Excel 打开该文件查看。`
            }
          }
          if (!wroteBack && !fileHandle) {
            try {
              const mode = await saveAdminMemberImportResultWithPicker(b64, originalName)
              if (mode === 'saved') {
                msg += hasFail
                  ? ` 请打开保存的「${originalName}」查看第 1 列「导入结果」。`
                  : ` 请打开保存的「${originalName}」查看第 1 列「导入结果」。`
              } else {
                msg += ' 已取消保存结果文件。'
              }
            } catch (_) {
              msg += hasFail
                ? ' 结果文件保存失败，请检查浏览器权限后重试。'
                : ' 结果文件保存失败，请检查浏览器权限后重试。'
            }
          }
        } else if (hasFail) {
          msg += ' 未生成结果文件，请重试导入。'
        }
        this.importSummaryFail = hasFail
        this.importSummary = msg
        await this.load()
      } catch (e) {
        this.importSummaryFail = true
        this.importSummary = e.message || '导入失败'
      }
    },
    isChecked(id) {
      return this.selectedMemberIds.includes(id)
    },
    toggleChecked(id) {
      const i = this.selectedMemberIds.indexOf(id)
      if (i >= 0) this.selectedMemberIds.splice(i, 1)
      else this.selectedMemberIds.push(id)
    },
    toggleCheckAll() {
      if (this.allChecked) {
        const ids = new Set(this.paginatedList.map((x) => x.id))
        this.selectedMemberIds = this.selectedMemberIds.filter((id) => !ids.has(id))
        return
      }
      const set = new Set(this.selectedMemberIds)
      this.paginatedList.forEach((x) => set.add(x.id))
      this.selectedMemberIds = Array.from(set)
    },
    openCreate() {
      this.editId = null
      this.form = createEmpty()
      this.errorMsg = ''
      this.loadApplyDutyOptionsFromMembershipForm()
      this.loadServiceConsultants()
      this.showDialog = true
    },
    openEdit(row) {
      this.editId = row.id
      this.errorMsg = ''
      const biz = this.resolveFormBusinessStatus(row)
      this.form = {
        memberCode: row.memberCode || '',
        nickname: row.nickname || '',
        avatarUrl: row.avatarUrl || '',
        mobile: row.mobile || '',
        companyName: row.registerCompanyName || row.companyName || '',
        registerTitle: row.registerTitle || '',
        serviceConsultant: row.serviceConsultant || '',
        memberLevel: row.memberLevel || '',
        status: biz,
        validUntil: row.validUntil || '',
        validUntilManual: !!row.validUntilManual,
        memberSort: Number(row.memberSort || 1) || 1,
        showInUnitList: row.showInUnitList !== false,
        showInNetworkList: row.showInNetworkList !== false,
        withdrawn: biz === 'WITHDRAW',
      }
      this.loadApplyDutyOptionsFromMembershipForm()
      this.loadServiceConsultants()
      this.showDialog = true
    },
    onValidUntilChange() {
      if (this.validUntilEditable) {
        this.form.validUntilManual = true
      }
    },
    async submit() {
      try {
        const nickname = String(this.form.nickname || '').trim()
        const mobile = String(this.form.mobile || '').trim()
        const companyName = String(this.form.companyName || '').trim()
        const registerTitle = String(this.form.registerTitle || '').trim()
        const memberLevel = this.form.memberLevel != null ? String(this.form.memberLevel).trim() : ''
        if (!nickname) throw new Error('姓名必填')
        if (!mobile) throw new Error('注册手机号必填')
        if (!/^1\d{10}$/.test(mobile)) throw new Error('注册手机号须为11位且以1开头')
        if (!companyName) throw new Error('注册人单位必填')
        if (!registerTitle) throw new Error('注册人单位职务必填')
        const biz = String(this.form.status || 'REGISTERED_IDLE').trim().toUpperCase()
        const isActive = biz === 'ACTIVE'
        const isWithdraw = biz === 'WITHDRAW' || !!this.form.withdrawn
        const validUntilRaw = this.form.validUntil ? String(this.form.validUntil).trim() : ''
        if (isActive && !memberLevel) throw new Error('正式会员须选择会员级别')
        if (isActive && !validUntilRaw) throw new Error('正式会员须填写有效期')
        if (memberLevel && !isActive && !isWithdraw) {
          throw new Error('已填写会员级别时，状态须选「正式会员」')
        }
        // 下拉四态 → 后端 status_code：ACTIVE / PENDING / WITHDRAW
        let status = 'PENDING'
        if (isActive) status = 'ACTIVE'
        else if (isWithdraw) status = 'WITHDRAW'
        const payload = {
          nickname,
          avatarUrl: String(this.form.avatarUrl || '').trim(),
          mobile,
          companyName,
          registerTitle,
          serviceConsultant: String(this.form.serviceConsultant || '').trim(),
          memberLevel,
          status: isWithdraw ? 'PENDING' : status,
          validUntil: isActive ? validUntilRaw : null,
          validUntilManual: isActive ? !!this.form.validUntilManual : false,
          memberSort: Number(this.form.memberSort || 1) || 1,
          showInUnitList: !!this.form.showInUnitList,
          showInNetworkList: !!this.form.showInNetworkList,
          withdrawn: isWithdraw,
        }
        const wx = this.form.wxOpenid != null ? String(this.form.wxOpenid).trim() : ''
        if (wx) payload.wxOpenid = wx

        let savedId = this.editId
        if (this.editId) {
          const updated = await updateAdminMember(this.editId, payload)
          if (updated && typeof updated === 'object' && updated.id != null) {
            savedId = updated.id
            const idx = (this.list || []).findIndex((x) => Number(x.id) === Number(updated.id))
            if (idx >= 0) {
              const merged = { ...this.list[idx], ...updated }
              this.list.splice(idx, 1, merged)
            }
          }
        } else {
          const created = await createAdminMember(payload)
          savedId = created && created.id != null ? created.id : savedId
        }
        if (biz === 'APPLY_REVIEW' && savedId) {
          try {
            await ensureMembershipApplyForMember(savedId)
          } catch (_) {
            /* 申请表补录失败不阻断主档保存 */
          }
        }

        this.showDialog = false
        await this.load()

        if (isActive) {
          // 正式会员需入会申请表；单位地图选点仅小程序可完成
          if (savedId) {
            try {
              await ensureMembershipApplyForMember(savedId)
            } catch (_) {
              /* 有表最好，没有也不阻断 */
            }
          }
          window.alert(
            '已保存为正式会员。\n\n'
              + '请通知该会员在小程序端打开并填写入会申请表（含单位名称、会员代表、单位地址等）。\n'
              + '单位地址的地图选点只能在手机微信小程序里完成，电脑后台无法选点。\n'
              + '也可在本页对该会员点「查看申请表」由会务代填文字项，地图选点仍须手机端完成。',
          )
        }
      } catch (e) {
        this.errorMsg = e.message || '保存失败'
      }
    },
    async revokeSessions(row) {
      if (!row || row.id == null) return
      if (!row.online) {
        this.errorMsg = '该会员当前离线，无需踢下线'
        return
      }
      if (!await adminConfirm(`确定踢下线该会员吗？（ID=${row.id}）`)) return
      try {
        await revokeAdminMemberSessions(row.id)
        this.errorMsg = ''
        window.alert('已踢下线：该会员需要重新登录才能继续操作')
      } catch (e) {
        this.errorMsg = e.message || '踢下线失败'
      }
    },
    isWithdrawnMember(row) {
      if (!row) return false
      const status = String(row.status || '').toUpperCase()
      return status === 'WITHDRAW' || !!row.withdrawn
    },
    /** 仅正式会员（非退会）展示「成员信息」代管入口 */
    memberRowShowPersonnel(row) {
      if (!row || this.isWithdrawnMember(row)) return false
      return String(row.status || '').toUpperCase() === 'ACTIVE'
    },
    openPersonnel(row) {
      const id = Number(row?.id || 0)
      if (!id) {
        this.errorMsg = '会员无效'
        return
      }
      if (!this.memberRowShowPersonnel(row)) {
        this.errorMsg = '仅正式会员可管理单位成员信息'
        return
      }
      const company = String(row.companyName || row.registerCompanyName || '').trim()
      this.$router
        .push({
          path: `/member/personnel/${id}`,
          query: company ? { company } : {},
        })
        .catch(() => {})
    },
    async deleteWithdrawnMember(row) {
      if (!row || row.id == null) return
      if (!this.canEdit) {
        this.errorMsg = '当前账号无会员编辑权限'
        return
      }
      if (!this.isWithdrawnMember(row)) {
        this.errorMsg = '仅已退会会员可删除'
        return
      }
      const name = String(row.nickname || row.memberCode || row.representativeName || '').trim() || `ID=${row.id}`
      const ok = await adminConfirm(
        `确定永久删除「${name}」及账号相关数据吗？删除后不可恢复。`,
        { title: '删除已退会会员', okText: '删除', cancelText: '取消' },
      )
      if (!ok) return
      try {
        await deleteAdminWithdrawnMember(row.id)
        this.errorMsg = ''
        window.alert('已删除该会员')
        await this.load()
      } catch (e) {
        this.errorMsg = e.message || '删除失败'
      }
    },
    feeVoucherButtonTitle(row) {
      const st = String(row?.feeStatus || '').toUpperCase()
      if (st === 'REVIEWING') return '查看凭证并审核通过或驳回'
      return '查看已上传的会费缴费凭证'
    },
    feeVoucherAbs(url) {
      const raw = String(url || '').trim()
      if (!raw) return ''
      if (/^https?:\/\//i.test(raw)) return raw
      return raw.startsWith('/') ? `${BASE_URL}${raw}` : `${BASE_URL}/${raw}`
    },
    memberHasSealFile(row) {
      if (!row) return false
      if (row.hasMembershipApplySealFile === true || row.has_membership_apply_seal_file === true) {
        return true
      }
      return !!String(row.membershipApplySealFileUrl || row.membership_apply_seal_file_url || '').trim()
    },
    /**
     * 会员管理操作列：打开该会员上传的入会申请盖章扫描件（文件）。
     */
    async viewMembershipApplySealFile(row) {
      if (!row || row.id == null) return
      let url = String(row.membershipApplySealFileUrl || row.membership_apply_seal_file_url || '').trim()
      let fileName = String(row.membershipApplySealFileName || row.membership_apply_seal_file_name || '').trim()
      try {
        if (!url) {
          const info = await getAdminMemberMembershipApplySealFile(row.id)
          if (info && info.hasSealFile) {
            url = String(info.sealFileUrl || '').trim()
            fileName = String(info.sealFileName || '').trim() || fileName
            row.hasMembershipApplySealFile = true
            row.membershipApplySealFileUrl = url
            row.membershipApplySealFileName = fileName
          }
        }
      } catch (e) {
        this.errorMsg = e?.message || '读取盖章件失败'
        return
      }
      if (!url) {
        window.alert('会员尚未上传盖章件')
        return
      }
      const abs = toMediaUrl(url) || this.feeVoucherAbs(url)
      if (!abs) {
        window.alert('盖章件地址无效')
        return
      }
      try {
        window.open(abs, '_blank', 'noopener')
      } catch (_) {
        window.alert(`无法打开盖章件${fileName ? `（${fileName}）` : ''}，请检查浏览器是否拦截弹窗`)
      }
    },
    async openFeeVoucherAudit(row, opts = {}) {
      if (!row) return
      const uploadOnly = !!opts.uploadOnly
      const viewOnly = !!opts.viewOnly
      const review = !!opts.review
      const orderId = Number(row?.feeOrderId || 0)
      const st = String(row?.feeStatus || '').toUpperCase()
      let img = ''
      let rejectNote = ''
      const raw = String(row?.feeVoucherUrl || '').trim()
      const isMock = raw.startsWith('mock://')
      if (raw && !isMock) img = this.feeVoucherAbs(raw)
      if (viewOnly && (String(row?.membershipPipelineLabel || '') === '入会缴费被驳回' || String(row?.renewalFeeHint || row?.renewal_fee_hint || '').includes('驳回'))) {
        rejectNote = String(row?.membershipFeeReviewNote || row?.feeReviewNote || '').trim()
      }
      const payOnline = String(row?.feePayMethod || row?.fee_pay_method || '').trim().toUpperCase() === 'ONLINE'
      const needsAttach = review && st === 'REVIEWING' && orderId > 0 && !img && (isMock || payOnline || !raw)
      const canUpload = (uploadOnly && orderId > 0) || needsAttach
      const canReview = review && st === 'REVIEWING' && orderId > 0 && !needsAttach
      if (viewOnly && !raw && !rejectNote) {
        window.alert('暂无会费缴费凭证')
        return
      }
      this.feeVoucherDlg = {
        visible: true,
        row,
        meta: `${row.nickname || row.registerName || '—'} · 订单 #${orderId || '—'} · ¥${this.feeMoneyText(row)}`,
        feeStatusLabel: row.feeStatusLabel || row.feeStatus || '—',
        img,
        canReview,
        canUpload,
        uploadOnly,
        viewOnly,
        mockVoucher: needsAttach && (isMock || payOnline),
        rejectNote,
        joinPaymentMode: false,
        submissionId: 0,
        busy: false,
        errorMsg: '',
        pendingVoucherUrl: '',
      }
    },
    pickFeeVoucherFile() {
      const el = this.$refs.feeVoucherFileInput
      if (el && typeof el.click === 'function') el.click()
    },
    async onFeeVoucherFilePicked(e) {
      const file = e && e.target && e.target.files && e.target.files[0]
      if (e && e.target) e.target.value = ''
      if (!file) return
      const row = this.feeVoucherDlg.row
      const orderId = Number(row?.feeOrderId || 0)
      if (!orderId) {
        this.errorMsg = '未找到会费订单'
        return
      }
      this.feeVoucherDlg.busy = true
      this.feeVoucherDlg.errorMsg = ''
      this.errorMsg = ''
      try {
        const data = await uploadImage(file)
        const url = String((data && data.url) || '').trim()
        if (!url) throw new Error('未获取到图片地址')
        if (this.feeVoucherDlg.uploadOnly) {
          this.feeVoucherDlg.pendingVoucherUrl = url
          this.feeVoucherDlg.img = this.feeVoucherAbs(url)
          return
        }
        const st = String(row?.feeStatus || '').toUpperCase()
        if (st === 'PENDING' || st === 'REJECTED') {
          await submitAdminMemberOrderOfflineVoucher(orderId, { voucherUrl: url })
        } else {
          await attachAdminMemberOrderVoucher(orderId, { voucherUrl: url })
        }
        this.feeVoucherDlg.img = this.feeVoucherAbs(url)
        this.feeVoucherDlg.canUpload = false
        this.feeVoucherDlg.mockVoucher = false
        this.feeVoucherDlg.canReview = true
      } catch (err) {
        this.feeVoucherDlg.errorMsg = err.message || '上传凭证失败'
      } finally {
        this.feeVoucherDlg.busy = false
      }
    },
    async confirmFeeVoucherUpload() {
      if (!this.canEdit) {
        window.alert('当前账号无会员编辑权限')
        return
      }
      const row = this.feeVoucherDlg.row
      const orderId = Number(row?.feeOrderId || 0)
      const url = String(this.feeVoucherDlg.pendingVoucherUrl || '').trim()
      if (!orderId) {
        this.feeVoucherDlg.errorMsg = '未找到会费订单'
        return
      }
      if (!url) {
        this.feeVoucherDlg.errorMsg = '请先选择凭证图片'
        return
      }
      this.feeVoucherDlg.busy = true
      this.feeVoucherDlg.errorMsg = ''
      try {
        const st = String(row?.feeStatus || '').toUpperCase()
        if (st === 'PENDING' || st === 'REJECTED') {
          await submitAdminMemberOrderOfflineVoucher(orderId, { voucherUrl: url })
        } else {
          await attachAdminMemberOrderVoucher(orderId, { voucherUrl: url })
        }
        this.closeFeeVoucherAudit()
        await this.load()
        window.alert('凭证已上传，状态已更新为待审核')
      } catch (err) {
        this.feeVoucherDlg.errorMsg = err.message || '提交凭证失败'
      } finally {
        this.feeVoucherDlg.busy = false
      }
    },
    closeFeeVoucherAudit() {
      this.feeVoucherDlg = {
        visible: false,
        row: null,
        meta: '',
        feeStatusLabel: '',
        img: '',
        canReview: false,
        canUpload: false,
        uploadOnly: false,
        viewOnly: false,
        pendingVoucherUrl: '',
        mockVoucher: false,
        rejectNote: '',
        joinPaymentMode: false,
        submissionId: 0,
        busy: false,
        errorMsg: '',
      }
    },
    async submitFeeVoucherApprove() {
      if (!this.canEdit) {
        window.alert('当前账号无会员编辑权限')
        return
      }
      const row = this.feeVoucherDlg.row
      const orderId = Number(row?.feeOrderId || 0)
      if (!orderId) {
        this.errorMsg = '未找到会费订单'
        return
      }
      if (!await adminConfirm('确认通过该线下缴费凭证？')) return
      this.feeVoucherDlg.busy = true
      this.errorMsg = ''
      try {
        await approveAdminMemberOrderOfflinePay(orderId, {})
        window.alert('已审核通过')
        this.closeFeeVoucherAudit()
        await this.load()
      } catch (e) {
        this.errorMsg = e.message || '审核通过失败'
      } finally {
        this.feeVoucherDlg.busy = false
      }
    },
    async submitFeeVoucherReject() {
      if (!this.canEdit) {
        window.alert('当前账号无会员编辑权限')
        return
      }
      const row = this.feeVoucherDlg.row
      const orderId = Number(row?.feeOrderId || 0)
      if (!orderId) {
        this.errorMsg = '未找到会费订单'
        return
      }
      const note = await adminPrompt('驳回原因（必填，会员端可见）：', '')
      if (note === null) return
      if (!String(note).trim()) {
        window.alert('请填写驳回原因')
        return
      }
      this.feeVoucherDlg.busy = true
      this.errorMsg = ''
      try {
        await rejectAdminMemberOrderOfflinePay(orderId, { note: String(note).trim() })
        window.alert('已驳回，会员可重新上传凭证')
        this.closeFeeVoucherAudit()
        await this.load()
      } catch (e) {
        this.errorMsg = e.message || '驳回失败'
      } finally {
        this.feeVoucherDlg.busy = false
      }
    },
    async showAuthIdentities(row) {
      if (!row || row.id == null) return
      this.refreshEditPermission()
      this.identityMemberId = row.id
      this.identityRow = row
      this.identityList = []
      this.identityLogList = []
      this.identityErrorMsg = ''
      this.showIdentityDialog = true
      try {
        this.identityList = (await getAdminMemberAuthIdentities(row.id)) || []
        this.identityLogList = (await getAdminMemberAuthIdentityLogs(row.id)) || []
      } catch (e) {
        this.identityErrorMsg = e.message || '读取账号绑定失败'
      }
    },
    openResetLocalPasswordFromIdentity() {
      this.openResetLocalPassword(this.identityRow || { id: this.identityMemberId })
    },
    openResetLocalPassword(row) {
      this.refreshEditPermission()
      if (!this.isSuperAdmin) {
        window.alert('仅超级管理员可重置会员登录密码')
        return
      }
      if (!row || row.id == null) return
      const mobile = String(row.registerMobile || row.mobile || '').trim()
      this.resetPwdForm = {
        memberId: row.id,
        name: String(row.registerName || row.nickname || row.name || '').trim(),
        mobile,
        password: '',
        password2: '',
      }
      this.resetPwdError = ''
      this.resetPwdBusy = false
      this.showResetPwdDialog = true
    },
    closeResetLocalPassword() {
      if (this.resetPwdBusy) return
      this.showResetPwdDialog = false
      this.resetPwdError = ''
      this.resetPwdForm = {
        memberId: null,
        name: '',
        mobile: '',
        password: '',
        password2: '',
      }
    },
    async submitResetLocalPassword() {
      this.refreshEditPermission()
      if (!this.isSuperAdmin) {
        this.resetPwdError = '仅超级管理员可重置会员登录密码'
        return
      }
      const memberId = this.resetPwdForm.memberId
      const mobile = String(this.resetPwdForm.mobile || '').trim()
      const pwd = String(this.resetPwdForm.password || '').trim()
      const pwd2 = String(this.resetPwdForm.password2 || '').trim()
      if (!memberId) {
        this.resetPwdError = '会员无效'
        return
      }
      if (!/^1\d{10}$/.test(mobile)) {
        this.resetPwdError = '该会员未设置有效的注册手机号，请先在「编辑」里补全手机号'
        return
      }
      if (pwd.length < 6 || pwd.length > 64) {
        this.resetPwdError = '密码长度需在 6～64 位'
        return
      }
      if (pwd !== pwd2) {
        this.resetPwdError = '两次输入的密码不一致'
        return
      }
      this.resetPwdBusy = true
      this.resetPwdError = ''
      try {
        const res = await resetAdminMemberLocalPassword(memberId, pwd)
        const created = !!(res && res.created === true)
        window.alert(
          created
            ? `已为该会员开通小程序登录账号。\n手机号：${mobile}\n请将新密码告知对方。`
            : `已重置小程序登录密码。\n手机号：${mobile}\n请将新密码告知对方。`
        )
        this.resetPwdBusy = false
        this.closeResetLocalPassword()
      } catch (e) {
        this.resetPwdError = e.message || '重置密码失败'
        this.resetPwdBusy = false
      }
    },
    async unbindIdentity(row) {
      if (!row || row.id == null || this.identityMemberId == null) return
      if (!this.canEdit) {
        window.alert('当前账号无会员编辑权限，无法解绑。')
        return
      }
      if (!await adminConfirm('确认解绑该登录方式吗？')) return
      try {
        await unbindAdminMemberAuthIdentity(this.identityMemberId, row.id)
        this.identityList = (await getAdminMemberAuthIdentities(this.identityMemberId)) || []
        this.identityLogList = (await getAdminMemberAuthIdentityLogs(this.identityMemberId)) || []
      } catch (e) {
        this.identityErrorMsg = e.message || '解绑失败'
      }
    },
  },
}
</script>

<style scoped>
.dlg-meta {
  margin: 0 0 8px;
  font-size: 13px;
  color: #475467;
}
.identity-pwd-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin: 0 0 12px;
  padding: 10px 12px;
  border: 1px solid #e4e7ec;
  border-radius: 8px;
  background: #f8fafc;
}
.voucher-img-wrap {
  margin: 0 0 12px;
  text-align: center;
  max-height: 52vh;
  overflow: auto;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  padding: 10px;
  background: #fafafa;
}
.voucher-img {
  max-width: 100%;
  height: auto;
  border-radius: 8px;
}
.dialog-fee-voucher {
  width: min(520px, 92vw);
}
.dialog-renewal {
  width: min(720px, 94vw);
}
.renewal-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 20px;
}
.renewal-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.renewal-field--full {
  grid-column: 1 / -1;
}
.renewal-field--readonly .renewal-readonly {
  font-size: 13px;
  color: #667085;
  line-height: 1.45;
  word-break: break-word;
}
.renewal-label {
  font-size: 13px;
  color: #344054;
  font-weight: 500;
}
.renewal-label--req::before {
  content: '*';
  color: #f04438;
  margin-right: 4px;
}
.renewal-input {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}
.renewal-radio-row {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
}
.renewal-radio {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #344054;
}
.renewal-voucher-wrap {
  max-height: none;
}
@media (max-width: 640px) {
  .renewal-grid {
    grid-template-columns: 1fr;
  }
}
.voucher-upload-add {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 160px;
  height: 160px;
  margin: 12px auto;
  border: 2px dashed #c5d0e6;
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  color: #5b6b8a;
}
.voucher-upload-add:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.voucher-upload-plus {
  font-size: 48px;
  line-height: 1;
  color: #98a8c8;
  font-weight: 300;
}
.voucher-upload-label {
  font-size: 13px;
}
.mock-voucher-hint {
  margin-top: 8px;
  font-size: 13px;
  line-height: 1.45;
}
.btn-link {
  border: none;
  background: transparent;
  color: #2f6af6;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
}
.btn-link:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.dlg-foot-hint {
  margin: 0;
  font-size: 13px;
}
.card { background: #fff; border-radius: 12px; padding: 16px; }
.head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; flex-wrap: wrap; margin-bottom: 12px; }
.head h2 { margin: 0 0 8px; }
.head-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: flex-end;
}
.import-summary {
  font-size: 13px;
  margin: 0 0 10px;
  line-height: 1.5;
}
.import-summary--ok {
  color: #067647;
}
.import-summary--fail {
  color: #d92d20;
}
.import-hint {
  font-size: 12px;
  line-height: 1.5;
  margin: 0 0 10px;
}
.need-fill-apply-toast {
  position: fixed;
  right: 20px;
  /* 叠在待办提醒上方：待办约 2 卡高 + 间距，避免互相遮挡 */
  bottom: 280px;
  z-index: 1300;
  width: min(380px, calc(100vw - 32px));
  max-height: min(360px, calc(100vh - 300px));
  overflow: auto;
  background: #fff;
  border: 1px solid #fec84b;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(16, 24, 40, 0.16);
  padding: 12px 14px;
}
.need-fill-apply-toast__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 6px;
}
.need-fill-apply-toast__head strong {
  color: #93370d;
  font-size: 14px;
}
.need-fill-apply-toast__close {
  border: 0;
  background: transparent;
  color: #667085;
  font-size: 20px;
  line-height: 1;
  cursor: pointer;
  padding: 0 2px;
}
.need-fill-apply-toast__desc {
  margin: 0 0 8px;
  font-size: 12px;
  line-height: 1.5;
  color: #475467;
}
.need-fill-apply-toast__list {
  margin: 0;
  padding: 0;
  list-style: none;
}
.need-fill-apply-toast__list li + li {
  margin-top: 4px;
}
.need-fill-apply-toast__link {
  border: 0;
  background: #fffaeb;
  color: #b54708;
  width: 100%;
  text-align: left;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.4;
  cursor: pointer;
}
.need-fill-apply-toast__link:hover {
  background: #fef0c7;
}
.need-fill-apply-toast__more {
  margin: 8px 0 0;
  font-size: 12px;
  color: #98a2b3;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
.filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 16px;
  margin: 12px 0 16px;
}
.flt {
  display: flex;
  align-items: center;
}
.flt-input {
  width: 180px;
  height: 40px;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 0 10px;
  background: #fff;
  font-size: 13px;
}
.flt-input--wide {
  width: 280px;
}
.flt-select {
  width: 170px;
}
:deep(.filters .fs-btn) {
  height: 40px;
  min-width: 170px;
  border-radius: 8px;
}
.fee-status-hint {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.35;
  color: #667085;
  max-width: 140px;
}
.row-highlight td {
  background: #fef0c7 !important;
  box-shadow: inset 0 0 0 1px #f79009;
}
.row-highlight .col-sticky-right {
  background: #fef0c7 !important;
}
.status-sub-hint {
  margin-top: 4px;
  font-size: 11px;
  line-height: 1.35;
  color: #b42318;
  max-width: 140px;
}
.preview-reject-wrap {
  grid-column: 1 / -1;
  margin-top: 4px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fef3f2;
  border: 1px solid #fecdca;
}
.preview-reject-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #b42318;
  margin-bottom: 4px;
}
.preview-reject-text {
  font-size: 13px;
  color: #912018;
  line-height: 1.45;
  white-space: pre-wrap;
}
.dlg-reject {
  margin: 8px 0 12px;
}
.voucher-upload-wrap {
  margin-bottom: 12px;
}
.dlg-online-tip {
  margin: 0 0 12px;
  color: #667085;
  font-size: 13px;
  line-height: 1.5;
}
.dialog-payment-records .dialog-body {
  max-height: min(70vh, 640px);
  overflow-y: auto;
}
.pay-records-sec {
  margin-bottom: 20px;
}
.pay-records-sec-title {
  margin: 0 0 8px;
  font-size: 15px;
  font-weight: 600;
  color: #101828;
}
.pay-records-hint {
  margin: 0 0 10px;
  font-size: 12px;
}
.pay-records-empty {
  padding: 8px 0 12px;
  font-size: 13px;
}
.pay-records-item {
  padding: 12px 0;
  border-top: 1px solid #eaecf0;
}
.pay-records-item:first-of-type {
  border-top: none;
}
.pay-records-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 6px;
}
.pay-records-title {
  flex: 1;
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}
.pay-records-status {
  font-size: 13px;
  color: #175cd3;
  flex-shrink: 0;
}
.pay-records-meta {
  font-size: 12px;
  color: #667085;
  line-height: 1.6;
}
.pay-records-voucher {
  margin-top: 8px;
  max-width: 100%;
  max-height: 160px;
  border-radius: 8px;
  border: 1px solid #eaecf0;
}
.filter-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.filter-action-btn {
  min-width: 108px;
  height: 40px;
  padding: 0 28px;
  font-size: 14px;
  font-weight: 600;
  border-radius: 8px;
  box-sizing: border-box;
}
.filter-actions .btn + .btn {
  margin-left: 0;
}
.table-scroll {
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  margin-top: 8px;
  border: 1px solid #edf0f5;
  border-radius: 10px;
  background: #fff;
}
.member-table {
  width: max-content;
  min-width: 100%;
  border-collapse: separate;
  border-spacing: 0;
}
.member-table th,
.member-table td {
  border-bottom: 1px solid #edf0f5;
  padding: 10px 12px;
  font-size: 13px;
  text-align: center;
  vertical-align: middle;
  white-space: nowrap;
  background: #fff;
}
.member-table thead th {
  background: #f8fafc;
  color: #344054;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 1;
}
.col-check { width: 44px; text-align: center; }
.col-seq { width: 56px; text-align: center; color: #667085; }
.col-company { min-width: 160px; text-align: center; }
.col-level { min-width: 120px; text-align: center; }
.col-consultant { min-width: 100px; text-align: center; }
.col-rep,
.col-reg {
  min-width: 128px;
  white-space: normal;
  vertical-align: middle;
}
.col-rep > div,
.col-reg > div {
  text-align: center;
  line-height: 1.45;
}
.col-date { min-width: 108px; }
.col-datetime { min-width: 168px; }
.col-tags {
  min-width: 120px;
  white-space: normal;
}
.tag-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: center;
}
.member-tag-pill {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  line-height: 18px;
  color: #0f766e;
  background: #f0fdf9;
  border: 1px solid #99f6e4;
}
.col-num { min-width: 72px; text-align: center; }
.col-status,
.col-money {
  text-align: center;
  vertical-align: middle;
}
.col-money {
  min-width: 88px;
  max-width: 150px;
  font-variant-numeric: tabular-nums;
  white-space: normal;
  color: #101828;
}
.fee-miss-link {
  border: 0;
  padding: 0;
  background: transparent;
  color: #d92d20;
  font-size: 12px;
  line-height: 1.35;
  cursor: pointer;
  text-align: left;
  text-decoration: underline;
}
.fee-miss-link:hover {
  color: #b42318;
}
.fee-miss-hint {
  margin-top: 2px;
  font-size: 11px;
  color: #98a2b3;
  line-height: 1.35;
  text-align: left;
}
.ellipsis {
  max-width: 160px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.col-sticky-right {
  position: sticky;
  right: 0;
  z-index: 2;
  box-shadow: -6px 0 10px rgba(15, 23, 42, 0.06);
}
.member-table thead .col-sticky-right {
  z-index: 3;
  background: #f8fafc;
}
.col-ops {
  min-width: 304px;
  white-space: normal;
}
.col-ops-grid {
  --op-btn-w: 144px;
  display: grid;
  grid-template-columns: repeat(2, var(--op-btn-w));
  gap: 8px;
  width: calc(var(--op-btn-w) * 2 + 8px);
  margin: 0 auto;
}
.op-btn {
  width: var(--op-btn-w);
  min-height: 32px;
  padding: 6px 10px;
  font-size: 12px;
  line-height: 1.25;
  border-radius: 8px;
  border: 1px solid transparent;
  cursor: pointer;
  white-space: nowrap;
  box-sizing: border-box;
  text-align: center;
}
.op-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.op-btn--bind {
  background: #eef4ff;
  color: #1d4ed8;
  border-color: #c7d7fe;
}
.op-btn--fee {
  background: #fff7e6;
  color: #b54708;
  border-color: #fedf89;
}
.op-btn--renewal {
  background: #f4f3ff;
  color: #5925dc;
  border-color: #d9d6fe;
}
.op-btn--records {
  background: #f0f9ff;
  color: #026aa2;
  border-color: #b9e6fe;
}
.op-btn--apply {
  background: #f5f3ff;
  color: #6d28d9;
  border-color: #ddd6fe;
}
.op-btn--personnel {
  background: #ecfdf5;
  color: #047857;
  border-color: #a7f3d0;
}
.op-btn--seal {
  background: #fff1f2;
  color: #be123c;
  border-color: #fecdd3;
}
.op-btn--edit {
  background: #2f6af6;
  color: #fff;
  border-color: #2f6af6;
}
.op-btn--tag {
  background: #f0fdf9;
  color: #0f766e;
  border-color: #99f6e4;
}
.tag-chip-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  min-height: 80px;
}
.tag-dialog-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 0 0 10px;
}
.tag-dialog-label {
  font-size: 13px;
  color: #344054;
  font-weight: 600;
}
.tag-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border: 1px solid #d0d7e2;
  border-radius: 999px;
  font-size: 13px;
  cursor: pointer;
  background: #fff;
}
.tag-chip--on {
  border-color: #2f6af6;
  background: #eef4ff;
  color: #1d4ed8;
}
.tag-chip input {
  display: none;
}
.op-btn--kick {
  background: #fff1f0;
  color: #d92d20;
  border-color: #ffc8c2;
}
.op-btn--delete {
  background: #d92d20;
  color: #fff;
  border-color: #d92d20;
}
.op-btn--delete:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
.table-empty {
  text-align: center;
  color: #98a2b3;
  padding: 32px 12px !important;
}
.sub { color: #667085; font-size: 12px; margin-top: 2px; white-space: normal; }
.col-rep .sub,
.col-reg .sub {
  white-space: nowrap;
}
.btn { border-radius: 8px; padding: 6px 12px; cursor: pointer; border: 1px solid transparent; font-size: 12px; }
.btn + .btn { margin-left: 8px; }
.btn-primary { background: #2f6af6; color: #fff; }
.btn-edit { border-color: #bfc9db; background: #fff; color: #26457d; }
.btn-cancel { border-color: #d0d7e2; background: #fff; color: #344054; }
.error { color: #d92d20; }
.dialog-form-error {
  margin: 0 0 10px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #fef3f2;
  border: 1px solid #fecdca;
  font-size: 13px;
  line-height: 1.4;
}
.status-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  line-height: 18px;
  white-space: nowrap;
}
.status-pill--active { background: #e8fbef; color: #067647; }
.status-pill--review { background: #fff7e6; color: #b54708; }
.status-pill--pay { background: #eef4ff; color: #1d4ed8; }
.status-pill--idle { background: #f2f4f7; color: #475467; }
.status-pill--biz { background: #f5f3ff; color: #6d28d9; }
.form { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.field-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}
.field-check-row {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  align-items: stretch;
}
.field { display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: #344054; }
.field > span { font-weight: 600; }
.field-check {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-height: 40px;
  padding: 8px 12px;
  border: 1px solid #eaecf0;
  border-radius: 8px;
  background: #fcfdff;
  box-sizing: border-box;
}
.field-check > span {
  flex: 1 1 auto;
  min-width: 0;
  font-weight: 600;
  font-size: 12px;
  line-height: 1.35;
}
.field-check > input[type='checkbox'] {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  margin: 0;
  cursor: pointer;
}
.field-full { grid-column: span 2; }
.field-hint {
  font-size: 12px;
  font-weight: normal;
  color: #667085;
  line-height: 1.45;
}
.dialog-hint {
  font-size: 12px;
  margin: 0 0 10px;
  color: #667085;
  line-height: 1.45;
}
.form-control {
  width: 100%;
  height: 40px;
  box-sizing: border-box;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 0 12px;
  background: #fcfdff;
  font-size: 13px;
  color: #101828;
}
.form-control--date:not(:disabled) {
  cursor: pointer;
}
.form-control[readonly],
.form-control:disabled {
  background: #f2f4f7;
  color: #98a2b3;
  cursor: not-allowed;
}
.field--disabled > span {
  color: #98a2b3;
}
.field-select {
  width: 100%;
  max-width: none;
}
:deep(.field-select .fs-btn) {
  height: 40px;
  border-radius: 8px;
  width: 100%;
  max-width: none;
  border: 1px solid #d0d7e2;
  background: #fcfdff;
  box-sizing: border-box;
}
.dialog-apply-preview {
  width: min(900px, calc(100vw - 48px));
}
.preview-meta {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px 12px;
  margin-bottom: 12px;
  color: #475467;
  font-size: 13px;
}
.preview-list {
  border-top: 1px solid #edf0f5;
  padding-top: 8px;
}
.preview-item {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 8px;
  padding: 8px 0;
  border-bottom: 1px dashed #edf0f5;
}
.preview-k {
  color: #111827;
  font-weight: 600;
  word-break: break-all;
}
.preview-v {
  color: #475467;
  word-break: break-word;
  white-space: pre-wrap;
}
.preview-item--group {
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
}
.preview-item--group .preview-k {
  grid-column: 1 / 3;
  color: #1d4ed8;
}
.preview-item--group .preview-v {
  display: none;
}
.media-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.media-thumb {
  width: 96px;
  height: 96px;
  object-fit: cover;
  border-radius: 8px;
  border: 1px solid #dbe2ef;
  cursor: pointer;
  background: #fff;
}
.file-name {
  margin-left: 8px;
  font-size: 12px;
  color: #667085;
}
.actions { margin-top: 10px; text-align: right; }
.inline-check {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-weight: normal;
}
.inline-check input[type='checkbox'] {
  width: 18px;
  height: 18px;
  min-width: 18px;
  min-height: 18px;
  margin: 0;
  flex-shrink: 0;
  accent-color: #2f6af6;
  cursor: pointer;
}
.inline-check-text {
  flex: 1;
  line-height: 1.45;
  font-size: 13px;
  font-weight: normal;
  color: #344054;
}
</style>

